// TODO: Replace MOCK_CONVERSATIONS with real API call to
// GET /manager/conversations/{tenantId} when Phase 2 backend is built.

import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { compressImageForUpload } from '../lib/compressImage'
import * as Sentry from '@sentry/react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  CircleEllipsis,
  Download,
  FileText,
  Grid2x2,
  Image,
  MessageSquareText,
  Paperclip,
  Pencil,
  Search,
  Send,
  SquarePen,
} from 'lucide-react'
import AppShell from '../components/layout/AppShell'
import DesktopShell from '../components/layout/DesktopShell'
import useManagerStore from '../store/managerStore'
import QuickAnswerTag from '../components/inbox/QuickAnswerTag'
import ImageBatchPreview from '../components/inbox/ImageBatchPreview'
import { sendErrorKey } from '../components/inbox/imageSendErrors'
import { deriveDeliveryState, isDeliveryFailed } from '../components/inbox/messageDeliveryState'
import { useImageBatchSend } from '../hooks/useImageBatchSend'
import useInboxUiStore, { DEFAULT_INBOX_UI_STATE } from '../store/inboxUiStore'
import { assignConversation, listConversationStaff, listConversations, getContactMessages, markConversationRead, resumeHandoffForConversation, sendConversationImages, toggleAIForConversation, uploadConversationImage } from '../api/conversations'
import { patchContact } from '../api/contacts'
import { useAuthStore } from '../store/authStore'
import { canStaffReply, handleReplyPaste, isSendEnter } from '../utils/replyInput'
import { useWsEvent, useWsSession } from '../lib/realtime/wsSessionHooks'
import { mergeMessageIntoDetailCache } from '../lib/realtime/wsMessages'
import { WS_RESYNC_EVENT_TYPE } from '../lib/realtime/wsClient'
import {
  createApplyWsMessageToConversationList,
  createApplyConversationListUpsert,
  dedupeConversationsById,
} from '../lib/realtime/inboxConversationListMerge'
import ContactTagsPanel from '../components/contacts/ContactTagsPanel'
import HandoffBanner from '../components/inbox/HandoffBanner'
import HandoffPill from '../components/inbox/HandoffPill'
import QuickAnswerSuggestion from '../components/inbox/QuickAnswerSuggestion'
import { botToggleAction, isBotReplying, isContactHandedOff } from '../utils/handoff'
import EditContactModal from '../components/contacts/EditContactModal'
import { normalizeContactTags } from '../utils/contactTags'
import normalizeSearchText from '../utils/normalizeSearchText'
import { formatContactDob, formatContactGender } from '../utils/contactFields'

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const UNASSIGNED_STAFF_FILTER = '__unassigned__'
const CHAT_NEAR_BOTTOM_THRESHOLD = 100

const inboxKeys = {
  all: ['inbox'],
  tenant: (tenantId) => ['inbox', tenantId],
  conversations: (tenantId) => ['inbox', tenantId, 'conversations'],
  conversationList: (tenantId, staffFilter, serverChannel) => ['inbox', tenantId, 'conversations', { staffFilter, serverChannel }],
  detail: (tenantId, contactId, channel) => ['inbox', tenantId, 'detail', contactId, normalizeChannelKey(channel)],
  staff: (tenantId) => ['inbox', tenantId, 'staff'],
}

function isSameCalendarDay(a, b, timeZone = 'Asia/Ho_Chi_Minh') {
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
  return fmt.format(a) === fmt.format(b)
}

function formatShortDateTime(d, timeZone = 'Asia/Ho_Chi_Minh') {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d)
  const get = (type) => parts.find(p => p.type === type)?.value
  return `${get('day')}/${get('month')} ${get('hour')}:${get('minute')}`
}

function relativeTime(iso, tr) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  if (!isSameCalendarDay(d, new Date())) return formatShortDateTime(d)
  const diff  = Date.now() - d.getTime()
  const mins  = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  if (hours > 0) return tr('inbox.relative.hoursAgo', { n: hours })
  if (mins  > 0) return tr('inbox.relative.minsAgo', { n: mins })
  return tr('inbox.relative.justNow')
}

function formatTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  if (!isSameCalendarDay(d, new Date())) return formatShortDateTime(d)
  return new Intl.DateTimeFormat('vi-VN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Ho_Chi_Minh',
  }).format(d)
}

function SpinnerRing({ size = 36, color = '#1535c8', strokeWidth = 3 }) {
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const dash = circumference * 0.28
  const gap = circumference * 0.72
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={`${dash} ${gap}`}
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          from={`0 ${size / 2} ${size / 2}`}
          to={`360 ${size / 2} ${size / 2}`}
          dur="0.8s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  )
}

// Delivery state under a message bubble. The state itself is derived in
// deriveDeliveryState(), which reads both the optimistic `_status` and the
// server's own `metadata.delivery_failed` — see that module for why both are
// needed. A message with neither renders nothing, so every pre-existing
// thread looks exactly as it did.
function MessageDeliveryStatus({ message, onRetry }) {
  const { t } = useTranslation()
  const state = deriveDeliveryState(message)
  if (!state) return null

  if (state.kind === 'sending') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#9ca3af', marginTop: 2 }}>
        <span style={{ width: 9, height: 9, border: '1.5px solid #9ca3af', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
        {t('inbox.batchUploading')}
      </span>
    )
  }

  // Retry works from a freshly failed optimistic message and from the server's
  // record of one after a refetch or a reload, because state.retry is rebuilt
  // from image_url when the optimistic fields are gone.
  const canRetry = state.retriable && state.retry?.url && typeof onRetry === 'function'
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, color: '#ef4444', marginTop: 2 }}
      title={state.errorDetail || undefined}
    >
      <AlertCircle size={10} />
      {t(sendErrorKey(state.errorCode))}
      {canRetry && (
        <button
          type="button"
          onClick={() => onRetry(state.retry.clientRef, state.retry)}
          style={{ border: 'none', background: 'none', padding: 0, fontSize: 10, color: '#1535c8', cursor: 'pointer', textDecoration: 'underline' }}
        >
          {t('inbox.batchRetry')}
        </button>
      )}
    </span>
  )
}

// The single-image send paths below keep compressing to Zalo's 1MB ceiling
// regardless of channel, exactly as before this constant moved. The batch
// image flow resolves its ceiling per channel instead (see
// constants/channelLimits.js); changing these call sites to match would alter
// the behavior of the existing single-image flow, which is out of scope here.
const MAX_ZALO_IMAGE_SIZE_BYTES = 1024 * 1024

function pickFirstNonEmpty(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue
    if (typeof value === 'string') {
      if (value.trim() !== '') return value
      continue
    }
    return value
  }
  return null
}

function createSendMessageError(response) {
  const message = pickFirstNonEmpty(response?.error, response?.message, 'send message failed')
  const err = new Error(String(message))
  err.response = { data: response }
  return err
}

function assertSendMessageSucceeded(response) {
  if (response?.success === false) throw createSendMessageError(response)
  return response
}

function getZaloOASubscriptionToastMessage(err, t) {
  const data = err?.response?.data
  const rawMessage = pickFirstNonEmpty(data?.error, data?.message, err?.message)
  const normalized = String(rawMessage || '').toLowerCase()
  const isSubscriptionError =
    normalized.includes('zalo api error -224') ||
    normalized.includes('oa needs to upgrade oa tier package') ||
    normalized.includes('upgrade oa tier package')

  if (!isSubscriptionError) return null
  return t('inbox.zaloOASubscriptionError')
}

function showZaloOASubscriptionToast(err, t) {
  const message = getZaloOASubscriptionToastMessage(err, t)
  if (!message) return false
  toast.error(message, { toastId: 'zalo-oa-subscription-error' })
  return true
}

function normalizeTagList(...values) {
  const seen = new Set()
  const tags = []
  const addTag = (value) => {
    if (value === null || value === undefined) return
    if (Array.isArray(value)) {
      value.forEach(addTag)
      return
    }
    if (typeof value === 'object') {
      addTag(pickFirstNonEmpty(value.name, value.label, value.value, value.id))
      return
    }
    String(value).split(/[;,]/).forEach(part => {
      const label = part.trim()
      if (!label) return
      const key = normalizeSearchText(label)
      if (seen.has(key)) return
      seen.add(key)
      tags.push(label)
    })
  }
  values.forEach(addTag)
  return tags
}

function truthyFlag(value) {
  if (value === true || value === 1) return true
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === 'true' || normalized === '1' || normalized === 'yes'
  }
  return false
}

function isImportantConversation(raw, slots, tags) {
  const flags = [
    raw?.important,
    raw?.is_important,
    raw?.starred,
    raw?.is_starred,
    raw?.pinned,
    raw?.is_pinned,
    slots?.important,
    slots?.is_important,
  ]
  if (flags.some(truthyFlag)) return true

  const priority = normalizeSearchText(raw?.priority || raw?.conversation_priority || slots?.priority || slots?.importance || '')
  if (priority === 'important' || priority === 'high') return true
  return tags.some(tag => {
    const normalized = normalizeSearchText(tag)
    return normalized === 'important' || normalized === 'quan trong' || normalized === 'urgent' || normalized === 'uu tien'
  })
}

function getLatestMessage(messages) {
  if (!Array.isArray(messages) || !messages.length) return null
  return messages[messages.length - 1]
}

function getConversationPreview(latestMsg, t) {
  if (latestMsg?.image_url) return t('inbox.imagePreview')
  const firstFile = getFileAttachments(latestMsg)[0]
  if (firstFile) return firstFile.filename || t('inbox.filePreview')
  const content = latestMsg?.content ? latestMsg.content.trim() : ''
  if (content) {
    const withoutImages = content.replace(MARKDOWN_IMAGE_RE, '').trim()
    if (withoutImages) return withoutImages.slice(0, 60)
    if (content.includes('![')) return t('inbox.imagePreview')
    return content.slice(0, 60)
  }
  return ''
}

const MARKDOWN_IMAGE_RE = /!\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)/g

function getRenderableMessageText(message) {
  const content = typeof message?.content === 'string' ? message.content.trim() : ''
  if (!content || content === '[Người dùng gửi ảnh]') return ''
  return content
}

function getMessageParts(message) {
  const content = getRenderableMessageText(message)
  const parts = []
  let lastIndex = 0

  for (const match of content.matchAll(MARKDOWN_IMAGE_RE)) {
    const index = match.index ?? 0
    const before = content.slice(lastIndex, index).trim()
    if (before) parts.push({ type: 'text', value: before })
    parts.push({ type: 'image', alt: match[1] || 'image', url: match[2] })
    lastIndex = index + match[0].length
  }

  const after = content.slice(lastIndex).trim()
  if (after) parts.push({ type: 'text', value: after })
  if (!parts.length && message?.image_url) parts.push({ type: 'image', alt: 'image', url: message.image_url })
  return parts
}

function messageHasOnlyImage(message) {
  const parts = getMessageParts(message)
  return parts.length === 1 && parts[0].type === 'image'
}

function messageHasOnlyMedia(message) {
  if (messageHasOnlyImage(message)) return true
  return getFileAttachments(message).length > 0 && getMessageParts(message).length === 0
}

function getFileNameFromUrl(url) {
  if (!url) return 'attachment'
  const encodedName = url.split('/').pop()?.split('?')[0] || ''
  try {
    return decodeURIComponent(encodedName) || 'attachment'
  } catch {
    return encodedName || 'attachment'
  }
}

function getFileAttachments(message) {
  const attachments = Array.isArray(message?.attachments)
    ? message.attachments.filter(attachment => attachment?.kind === 'file' && attachment?.url)
    : []
  if (attachments.length) return attachments

  // Backward compatibility for messages persisted before attachments[] was introduced.
  if (message?.file_url) {
    return [{
      kind: 'file',
      url: message.file_url,
      download_url: message.download_url,
      filename: message.file_name || getFileNameFromUrl(message.file_url),
    }]
  }
  return []
}

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '')
}

function conversationSearchValues(conv) {
  const slots = conv?.slots || {}
  const channelMeta = Array.isArray(conv?.channelMeta) ? conv.channelMeta : []
  const metaValues = channelMeta.flatMap(meta => [
    meta?.channel,
    CHANNEL_META[normalizeChannelKey(meta?.channel)]?.title,
    meta?.conversation_id,
    meta?.user_id,
    meta?.preview,
    meta?.phone,
    meta?.stage,
    meta?.booking_id,
    ...(Array.isArray(meta?.tags) ? meta.tags : []),
    ...(Array.isArray(meta?.messages) ? meta.messages.slice(-5).map(message => message?.content) : []),
  ])

  return [
    conv?.name,
    conv?.preview,
    conv?.phone,
    conv?.email,
    conv?.address,
    conv?.notes,
    conv?.contact_id,
    conv?.stage,
    conv?.booking_id,
    slots.customer_name,
    slots.customer_display_name,
    slots.customer_phone,
    slots.customer_email,
    slots.email,
    slots.phone,
    ...(Array.isArray(conv?.tags) ? conv.tags : []),
    ...(Array.isArray(conv?.channels)
      ? conv.channels.flatMap(ch => [ch, CHANNEL_META[normalizeChannelKey(ch)]?.title])
      : []),
    ...metaValues,
  ].filter(value => value !== null && value !== undefined && String(value).trim() !== '')
}

function conversationMatchesSearch(conv, query) {
  const normalizedQuery = normalizeSearchText(query)
  if (!normalizedQuery) return true

  const values = conversationSearchValues(conv)
  const searchText = normalizeSearchText(values.join(' '))
  if (searchText.includes(normalizedQuery)) return true

  const queryDigits = digitsOnly(query)
  if (!queryDigits) return false
  return values.some(value => digitsOnly(value).includes(queryDigits))
}

function conversationMatchesListFilter(conv, filter) {
  if (filter === 'unread') return Number(conv?.unread || 0) > 0
  if (filter === 'important') return Boolean(conv?.important)
  return true
}

function staffDisplayName(staff) {
  return pickFirstNonEmpty(staff?.name, staff?.email, staff?.id, '')
}

function assignmentLabel(assignment, t) {
  return pickFirstNonEmpty(assignment?.assigned_staff_name, assignment?.assigned_staff_email, t('inbox.unassigned'))
}

function channelLabel(channel, t) {
  const key = normalizeChannelKey(channel)
  if (key === 'zalo') return t('inbox.channel.zalo')
  if (key === 'zalo_zbs') return t('inbox.channel.zalo_zbs')
  if (key === 'facebook') return t('inbox.channel.facebook')
  if (key === 'whatsapp') return t('inbox.channel.whatsapp')
  return key ? key.charAt(0).toUpperCase() + key.slice(1) : t('common.unknown')
}

function toUnifiedContacts(conversationRows, t) {
  const groups = new Map()

  ;(conversationRows || []).forEach(raw => {
    const channel = normalizeChannelKey(raw.channel)
    if (!channel) return
    const contactId = raw.contact_id || `unknown:${raw.user_id || raw.id || Math.random()}`
    if (!groups.has(contactId)) {
      groups.set(contactId, {
        id: `contact:${contactId}`,
        contact_id: contactId,
        channels: [],
        channelMetaByKey: {},
        name: null,
        avatar_url: null,
        phone: null,
        email: null,
        address: null,
        gender: null,
        dob: null,
        slots: {},
        preview: '',
        updated_at: null,
        mode: 'ai',
        channel_bot_enabled: true,
        unread: 0,
        stage: null,
        booking_id: null,
        notes: null,
        tags: [],
        assignedTags: [],
        important: false,
        assigned_staff_id: null,
        assigned_staff_name: null,
        assigned_staff_email: null,
        assigned_at: null,
      })
    }

    const group = groups.get(contactId)
    const slots = raw.slots || {}
    const assignedTags = normalizeContactTags(raw.tags)
    const tags = normalizeTagList(raw.tags, raw.labels, raw.contact_tags, raw.customer_tags, slots.tags, slots.labels, slots.customer_tags)
    const important = isImportantConversation(raw, slots, tags)
    const unread = Number(raw.unread_count ?? raw.unread ?? 0) || 0
    const latestMsg = getLatestMessage(raw.messages)

    const channelMeta = {
      channel,
      conversation_id: pickFirstNonEmpty(raw.conversation_id, raw.id),
      user_id: raw.user_id,
      unread,
      updated_at: raw.updated_at || null,
      mode: raw.ai_enabled ? 'ai' : 'staff',
      stage: raw.stage,
      booking_id: raw.booking_id,
      tags,
      important,
      slots,
      phone: pickFirstNonEmpty(raw.phone, slots.customer_phone),
      messages: Array.isArray(raw.messages) ? raw.messages : [],
      preview: getConversationPreview(latestMsg, t),
      assigned_staff_id: raw.assigned_staff_id || null,
      assigned_staff_name: raw.assigned_staff_name || null,
      assigned_staff_email: raw.assigned_staff_email || null,
      assigned_at: raw.assigned_at || null,
      channel_bot_enabled: raw.channel_bot_enabled !== false,
      ai_toggle_disabled: Boolean(raw.ai_toggle_disabled),
      // Handed off to staff: the bot is not replying until someone resumes
      // it. Independent of `mode` (the AI on/off toggle).
      handed_off: Boolean(raw.handed_off),
    }

    const existingMeta = group.channelMetaByKey[channel]
    if (!existingMeta) {
      group.channelMetaByKey[channel] = channelMeta
    } else {
      const existingTs = existingMeta.updated_at ? new Date(existingMeta.updated_at).getTime() : 0
      const nextTs = channelMeta.updated_at ? new Date(channelMeta.updated_at).getTime() : 0
      if (nextTs >= existingTs) {
        group.channelMetaByKey[channel] = {
          ...channelMeta,
          unread: existingMeta.unread + channelMeta.unread,
          tags: normalizeTagList(existingMeta.tags, channelMeta.tags),
          important: existingMeta.important || channelMeta.important,
        }
      } else {
        group.channelMetaByKey[channel] = {
          ...existingMeta,
          unread: existingMeta.unread + channelMeta.unread,
          tags: normalizeTagList(existingMeta.tags, channelMeta.tags),
          important: existingMeta.important || channelMeta.important,
        }
      }
    }

    group.name = pickFirstNonEmpty(
      group.name,
      raw.contact_name,
      slots.customer_display_name,
      slots.customer_name,
      raw.user_id?.slice(0, 12),
      t('common.unknown')
    )
    group.avatar_url = pickFirstNonEmpty(group.avatar_url, raw.avatar_url, raw.contact_avatar_url)
    group.phone = pickFirstNonEmpty(group.phone, raw.phone, slots.customer_phone)
    group.email = pickFirstNonEmpty(group.email, raw.email, raw.contact_email, raw.customer_email, slots.customer_email, slots.email)
    group.address = pickFirstNonEmpty(group.address, raw.address, raw.contact_address, raw.customer_address, slots.customer_address, slots.address)
    group.gender = pickFirstNonEmpty(group.gender, raw.gender, raw.contact_gender, slots.gender, slots.customer_gender)
    group.dob = pickFirstNonEmpty(group.dob, raw.dob, raw.date_of_birth, raw.contact_dob, slots.dob, slots.customer_dob, slots.date_of_birth)
    group.notes = pickFirstNonEmpty(group.notes, raw.notes, raw.contact_notes, raw.customer_notes, slots.notes, slots.customer_notes)
    group.slots = {
      ...slots,
      ...group.slots,
      customer_name: pickFirstNonEmpty(group.slots.customer_name, slots.customer_name, slots.customer_display_name),
      customer_phone: pickFirstNonEmpty(group.slots.customer_phone, slots.customer_phone, raw.phone),
    }
    group.tags = normalizeTagList(group.tags, tags)
    group.assignedTags = normalizeContactTags(group.assignedTags, assignedTags)
    group.important = group.important || important

    const groupTs = group.updated_at ? new Date(group.updated_at).getTime() : 0
    const rowTs = raw.updated_at ? new Date(raw.updated_at).getTime() : 0
    if (rowTs >= groupTs) {
      group.updated_at = raw.updated_at
      group.preview = channelMeta.preview
      group.mode = channelMeta.mode
      group.channel_bot_enabled = channelMeta.channel_bot_enabled
      group.stage = raw.stage
      group.booking_id = raw.booking_id
      group.default_channel = channel
      group.assigned_staff_id = channelMeta.assigned_staff_id
      group.assigned_staff_name = channelMeta.assigned_staff_name
      group.assigned_staff_email = channelMeta.assigned_staff_email
      group.assigned_at = channelMeta.assigned_at
    }
  })

  return [...groups.values()].map(group => {
    const channels = Object.keys(group.channelMetaByKey)
    const unreadByChannel = {}
    let totalUnread = 0
    channels.forEach(ch => {
      const unread = Number(group.channelMetaByKey[ch]?.unread || 0)
      unreadByChannel[ch] = unread
      totalUnread += unread
    })
    const tags = normalizeTagList(group.tags, ...channels.map(ch => group.channelMetaByKey[ch]?.tags || []))
    const assignedTags = normalizeContactTags(group.assignedTags)
    return {
      ...group,
      channels,
      unread: totalUnread,
      unreadByChannel,
      channelMeta: channels.map(ch => group.channelMetaByKey[ch]),
      tags,
      assignedTags,
      important: group.important || channels.some(ch => group.channelMetaByKey[ch]?.important),
      time: relativeTime(group.updated_at, t),
      activeChannel: group.default_channel || channels[0] || null,
    }
  })
}

function getChannelMeta(conv, channel) {
  if (!conv) return null
  const key = normalizeChannelKey(channel || conv.activeChannel)
  if (!key) return null
  return conv.channelMeta?.find(meta => normalizeChannelKey(meta.channel) === key) || null
}

// Track B (realtime-inbox migration) FE1.3/FE1.4 — see
// inboxConversationListMerge.js for what this does; instantiated once here
// (module scope) with this file's own preview/time/channel-key helpers so
// there's a single source of truth for that logic, not a duplicate.
const applyWsMessageToConversationList = createApplyWsMessageToConversationList({
  getConversationPreview,
  relativeTime,
  normalizeChannelKey,
})

// Track B2 (pagination-and-ws-tenant-wide) — see
// inboxConversationListMerge.js's createApplyConversationListUpsert() doc
// comment for what this does; instantiated once here (module scope) for
// the same reason as applyWsMessageToConversationList above.
const applyConversationListUpsert = createApplyConversationListUpsert({
  relativeTime,
  normalizeChannelKey,
})

function getReadableChannel(conv, preferredChannel = null) {
  if (!conv) return null
  const preferred = normalizeChannelKey(preferredChannel)
  const channels = (conv.channels || []).map(normalizeChannelKey).filter(Boolean)
  if (preferred && channels.includes(preferred)) return preferred
  const active = normalizeChannelKey(conv.activeChannel)
  if (active && channels.includes(active)) return active
  return channels[0] || null
}

function markContactChannelRead(conv, channel) {
  if (!conv) return conv
  const key = normalizeChannelKey(channel)
  if (!key) return conv

  const channelMeta = (conv.channelMeta || []).map(meta =>
    normalizeChannelKey(meta.channel) === key ? { ...meta, unread: 0 } : meta
  )
  const unreadByChannel = { ...(conv.unreadByChannel || {}), [key]: 0 }
  const unread = Object.values(unreadByChannel).reduce((sum, value) => sum + (Number(value) || 0), 0)

  return {
    ...conv,
    channelMeta,
    unreadByChannel,
    unread,
  }
}

// ─────────────────────────────────────────────
// Shared sub-components
// ─────────────────────────────────────────────

const CHANNEL_META = {
  zalo:     { image: '/zalo.png', title: 'Zalo' },
  zalo_zbs: { image: '/zalo.png', title: 'Zalo ZBS' },
  facebook: { image: '/mess.png', title: 'Messenger' },
  messenger: { image: '/mess.png', title: 'Messenger' },
  web:      { bg: '#6366f1', glyph: 'W', title: 'Web' },
  widget:   { bg: '#6366f1', glyph: 'W', title: 'Web' },
  whatsapp: { bg: '#25D366', glyph: 'WA', title: 'WhatsApp' },
}

function normalizeChannelKey(channel) {
  if (!channel) return null
  const key = String(channel).toLowerCase()
  if (key === 'widget') return 'web'
  if (key === 'messenger') return 'facebook'
  return key
}

function ChannelIconBubble({ channel, size = 16, style = {} }) {
  const key = normalizeChannelKey(channel)
  const meta = key ? CHANNEL_META[key] : null
  if (!meta) return null

  if (meta.image) {
    return (
      <span
        title={meta.title}
        style={{
          width: size,
          height: size,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          lineHeight: 0,
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.12))',
          ...style,
        }}
      >
        <img
          src={meta.image}
          alt=""
          draggable={false}
          style={{ width: size, height: size, objectFit: 'contain', display: 'block', pointerEvents: 'none' }}
        />
      </span>
    )
  }

  const bubbleStyle = {
    width: size,
    height: size,
    borderRadius: '50%',
    background: meta.bg,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxSizing: 'border-box',
    boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
    ...style,
  }
  return (
    <span style={bubbleStyle} title={meta.title}>
      <span style={{ color: 'white', fontSize: Math.max(7, Math.round(size * 0.38)), fontWeight: 700, lineHeight: 1 }}>{meta.glyph}</span>
    </span>
  )
}

function ChannelIconStack({ channels, size = 16, overlap = 5 }) {
  const list = (Array.isArray(channels) ? channels : channels ? [channels] : [])
    .map(normalizeChannelKey)
    .filter(Boolean)
  const unique = [...new Set(list)]
  if (!unique.length) return null
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
      {unique.map((ch, i) => (
        <ChannelIconBubble
          key={ch}
          channel={ch}
          size={size}
          style={{ marginLeft: i === 0 ? 0 : -overlap, zIndex: unique.length - i }}
        />
      ))}
    </span>
  )
}

function ModePill({ mode, t }) {
  if (mode === 'ai') {
    return <span className="pill" style={{ background: '#e8f0fe', color: '#1535c8', fontSize: 10 }}>{t('inbox.modeAi')}</span>
  }
  return <span className="pill" style={{ background: '#fff3e0', color: '#e65100', fontSize: 10 }}>{t('inbox.modeStaff')}</span>
}

// Compact on/off indicator for the AI bot's per-conversation state — green
// only when the bot is actually replying: the conversation's own toggle is
// on AND the channel it's on hasn't been disabled at the channel-settings
// level (channel_bot_enabled false overrides an 'ai' mode to "not really
// running", same as the channel-disabled banner in the thread view), AND the
// conversation isn't handed off to staff (the bot stays silent then too).
function AiBotStatusDot({ mode, channelBotEnabled, handedOff = false, t }) {
  const isOn = mode === 'ai' && channelBotEnabled !== false && !handedOff
  return (
    <span
      title={isOn ? t('inbox.aiBotOn') : t('inbox.aiBotOff')}
      aria-label={isOn ? t('inbox.aiBotOn') : t('inbox.aiBotOff')}
      style={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
        background: isOn ? '#22c55e' : '#c1c7d0',
        boxShadow: isOn ? '0 0 0 2px rgba(34,197,94,0.16)' : 'none',
      }}
    />
  )
}

function UnreadCountBadge({ count, compact = false }) {
  const unread = Number(count || 0)
  if (unread <= 0) return null
  const label = unread > 99 ? '99+' : String(unread)
  return (
    <span
      style={{
        minWidth: compact ? 14 : 16,
        height: compact ? 14 : 16,
        padding: compact ? '0 4px' : '0 5px',
        borderRadius: 999,
        background: '#ef4444',
        color: 'white',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: compact ? 9 : 10,
        fontWeight: 600,
        lineHeight: 1,
        boxSizing: 'border-box',
        flexShrink: 0,
      }}
    >
      {label}
    </span>
  )
}

function StaffFilterButton({ active, label, count, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        border: 'none',
        borderRadius: 7,
        background: active ? '#e8ecff' : 'transparent',
        color: active ? '#1535c8' : '#555',
        cursor: 'pointer',
        padding: '5px 8px',
        fontSize: 12,
        fontWeight: active ? 600 : 500,
        textAlign: 'left',
      }}
    >
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      {count !== null && count !== undefined && (
        <span style={{ flexShrink: 0, borderRadius: 999, background: active ? '#1535c8' : '#eef2ff', color: active ? 'white' : '#4f46e5', minWidth: 18, padding: '1px 5px', textAlign: 'center', fontSize: 10 }}>
          {count}
        </span>
      )}
    </button>
  )
}

function StaffFilterChip({ active, label, count, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: 32,
        maxWidth: 170,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        border: `0.5px solid ${active ? '#1535c8' : '#e0e3f0'}`,
        borderRadius: 9,
        background: active ? '#e8ecff' : 'white',
        color: active ? '#1535c8' : '#555',
        cursor: 'pointer',
        padding: '0 10px',
        fontSize: 12,
        fontWeight: active ? 700 : 600,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      {count !== null && count !== undefined && (
        <span style={{ flexShrink: 0, borderRadius: 999, background: active ? '#1535c8' : '#eef2ff', color: active ? 'white' : '#4f46e5', minWidth: 18, padding: '1px 5px', textAlign: 'center', fontSize: 10 }}>
          {count}
        </span>
      )}
    </button>
  )
}

function ChannelToggle({ channels, selectedChannel, unreadByChannel = {}, onSelect, t, compact = false }) {
  if (!Array.isArray(channels) || channels.length <= 1) return null
  const size = compact ? 10 : 11
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {channels.map(channel => {
        const key = normalizeChannelKey(channel)
        const isActive = key === normalizeChannelKey(selectedChannel)
        const unread = Number(unreadByChannel[key] || 0)
        return (
          <button
            key={key}
            onClick={() => onSelect?.(key)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              borderRadius: 999,
              border: `1px solid ${isActive ? '#1535c8' : '#d1d5db'}`,
              background: isActive ? '#e8ecff' : 'white',
              color: isActive ? '#1535c8' : '#374151',
              fontSize: size,
              fontWeight: isActive ? 600 : 500,
              padding: compact ? '2px 7px' : '3px 9px',
              cursor: 'pointer',
            }}
          >
            <span>{channelLabel(key, t)}</span>
            {unread > 0 && (
              <span style={{ minWidth: 14, textAlign: 'center', borderRadius: 999, padding: '0 4px', background: isActive ? '#1535c8' : '#eef2ff', color: isActive ? 'white' : '#4f46e5' }}>
                {unread}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

// The desktop Inbox keeps every channel as a separate tab.  The narrow
// sidebar cannot fit all labels at once, so allow both native touch scrolling
// and click-drag scrolling with the mouse.  A short drag is treated as a
// gesture rather than a tab click, preventing accidental filter changes.
function HorizontalTabScroller({ children, ariaLabel }) {
  const scrollerRef = useRef(null)
  const dragRef = useRef({
    pointerId: null,
    startX: 0,
    startScrollLeft: 0,
    moved: false,
    captured: false,
    suppressClick: false,
  })

  const handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    const element = scrollerRef.current
    if (!element) return
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: element.scrollLeft,
      moved: false,
      captured: false,
      suppressClick: false,
    }
    // Do not capture the pointer on a plain click: capturing during
    // pointerdown prevents the child tab button from receiving its click.
    // Capture only after movement crosses the drag threshold.
    element.style.cursor = 'grab'
  }

  const handlePointerMove = (event) => {
    const drag = dragRef.current
    const element = scrollerRef.current
    if (!element || drag.pointerId !== event.pointerId) return
    const delta = event.clientX - drag.startX
    if (Math.abs(delta) > 4) {
      drag.moved = true
      if (!drag.captured) {
        element.setPointerCapture?.(event.pointerId)
        drag.captured = true
        element.style.cursor = 'grabbing'
      }
    }
    if (drag.moved) element.scrollLeft = drag.startScrollLeft - delta
  }

  const handlePointerEnd = (event) => {
    const drag = dragRef.current
    const element = scrollerRef.current
    if (!element || drag.pointerId !== event.pointerId) return
    drag.suppressClick = drag.moved
    drag.pointerId = null
    element.releasePointerCapture?.(event.pointerId)
    element.style.cursor = 'grab'
  }

  const handlePointerCancel = (event) => {
    handlePointerEnd(event)
    // A cancelled pointer does not produce a click, so do not let the next
    // unrelated click get swallowed by the drag guard.
    dragRef.current.suppressClick = false
  }

  const handleClickCapture = (event) => {
    if (!dragRef.current.suppressClick) return
    event.preventDefault()
    event.stopPropagation()
    dragRef.current.suppressClick = false
  }

  return (
    <div
      ref={scrollerRef}
      role="tablist"
      aria-label={ariaLabel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerCancel}
      onClickCapture={handleClickCapture}
      style={{
        display: 'flex',
        gap: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'thin',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y',
        cursor: 'grab',
        userSelect: 'none',
        overscrollBehaviorX: 'contain',
      }}
    >
      {children}
    </div>
  )
}

function buildCustomerBasicInfoRows(conv, convDetail, slots, t) {
  if (!conv) return []
  const empty = t('contacts.emptyValue')
  const detailSlots = convDetail?.slots || {}
  const valueOrEmpty = (...values) => pickFirstNonEmpty(...values) || empty
  return [
    { label: t('contacts.phone'), value: valueOrEmpty(conv.phone, slots.customer_phone, detailSlots.customer_phone), truncate: true },
    { label: t('contacts.email'), value: valueOrEmpty(conv.email, slots.customer_email, slots.email, detailSlots.customer_email, detailSlots.email), truncate: true },
    { label: t('contacts.address'), value: valueOrEmpty(conv.address, slots.customer_address, slots.address, detailSlots.customer_address, detailSlots.address), truncate: true },
    {
      label: t('contacts.gender'),
      value: formatContactGender(pickFirstNonEmpty(conv.gender, convDetail?.gender, slots.gender, slots.customer_gender, detailSlots.gender, detailSlots.customer_gender), t),
      truncate: false,
    },
    {
      label: t('contacts.dob'),
      value: formatContactDob(pickFirstNonEmpty(conv.dob, convDetail?.dob, slots.dob, slots.customer_dob, slots.date_of_birth, detailSlots.dob, detailSlots.customer_dob, detailSlots.date_of_birth), t),
      truncate: false,
    },
  ]
}

const INBOX_EDIT_CONTACT_FIELDS = ['name', 'phone', 'email', 'address', 'gender', 'dob']

function buildCustomerEditContact(conv, convDetail, slots) {
  if (!conv) return {}
  const detailSlots = convDetail?.slots || {}
  return {
    contact_id: conv.contact_id,
    name: conv.name || '',
    phone: pickFirstNonEmpty(conv.phone, slots.customer_phone, detailSlots.customer_phone) || '',
    email: pickFirstNonEmpty(conv.email, slots.customer_email, slots.email, detailSlots.customer_email, detailSlots.email) || '',
    address: pickFirstNonEmpty(conv.address, slots.customer_address, slots.address, detailSlots.customer_address, detailSlots.address) || '',
    gender: pickFirstNonEmpty(conv.gender, convDetail?.gender, slots.gender, slots.customer_gender, detailSlots.gender, detailSlots.customer_gender) || '',
    dob: pickFirstNonEmpty(conv.dob, convDetail?.dob, slots.dob, slots.customer_dob, slots.date_of_birth, detailSlots.dob, detailSlots.customer_dob, detailSlots.date_of_birth) || '',
  }
}

function BasicInfoRow({ label, value, truncate = false }) {
  const text = String(value ?? '')
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, padding: '6px 0', borderBottom: '0.5px solid #f0f0f0' }}>
      <span style={{ color: '#888', flexShrink: 0 }}>{label}</span>
      <span
        title={truncate ? text : undefined}
        style={{
          color: '#111',
          fontWeight: 500,
          textAlign: 'right',
          minWidth: 0,
          flex: 1,
          ...(truncate ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {}),
        }}
      >
        {text}
      </span>
    </div>
  )
}

function MessageContent({ message, imageOnly = false }) {
  const parts = getMessageParts(message)
  const fallbackImageUrl = message?.image_url && !parts.some(part => part.type === 'image') ? message.image_url : null
  const renderParts = fallbackImageUrl ? [...parts, { type: 'image', alt: 'image', url: fallbackImageUrl }] : parts
  const fileAttachments = getFileAttachments(message)

  return (
    <>
      {renderParts.map((part, index) => {
        if (part.type === 'image') {
          return (
            <img
              key={`${part.url}-${index}`}
              src={part.url}
              alt={part.alt || 'image'}
              style={{
                maxWidth: '100%',
                maxHeight: 240,
                borderRadius: 8,
                border: imageOnly ? '1px solid #e5e7eb' : 'none',
                display: 'block',
                marginTop: index > 0 ? 8 : 0,
                cursor: 'pointer',
              }}
              onClick={() => window.open(part.url, '_blank')}
            />
          )
        }

        return (
          <p
            key={`text-${index}`}
            style={{
              margin: index > 0 ? '8px 0 0' : 0,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
            }}
          >
            {part.value}
          </p>
        )
      })}
      {fileAttachments.map((attachment, index) => {
        const href = attachment.download_url || attachment.url
        const fileName = attachment.filename || getFileNameFromUrl(attachment.url)
        return (
        <a
          key={`${attachment.url}-${index}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          download={fileName}
          style={{ marginTop: renderParts.length || index > 0 ? 8 : 0, minWidth: 190, maxWidth: '100%', padding: '9px 10px', borderRadius: 8, border: '1px solid #dbe3f4', background: '#f8fafc', color: '#1f2937', display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none' }}
        >
          <FileText size={20} color="#1535c8" style={{ flexShrink: 0 }} />
          <span title={fileName} style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 500 }}>{fileName}</span>
          <Download size={15} color="#6b7280" style={{ flexShrink: 0 }} />
        </a>
        )
      })}
    </>
  )
}

// OQ10 resolved (2026-07-29) -- ZNS delivery bubbles show the human-readable
// template_name (fallback template_id if not yet synced from eTelecom) plus
// the template variables actually sent (template_data), instead of relying on
// the backend-composed plain-text `content` for structure/emphasis.
function ZnsMessageBody({ message }) {
  const templateName = message?.metadata?.template_name || message?.metadata?.template_id
  const templateData = message?.metadata?.template_data
  const entries = templateData && typeof templateData === 'object' ? Object.entries(templateData) : []
  return (
    <>
      <p style={{ margin: 0, fontWeight: 600 }}>{templateName}</p>
      {entries.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 11, opacity: 0.85 }}>
          {entries.map(([key, value]) => (
            <div key={key}>{key}: {String(value)}</div>
          ))}
        </div>
      )}
    </>
  )
}

function ContactNotesPanel({ notes, disabled = false, saving = false, onSave, t }) {
  const original = notes ?? ''
  const [draft, setDraft] = useState(original)

  useEffect(() => {
    setDraft(original)
  }, [original])

  const isDirty = draft.trim() !== String(original).trim()
  const canSave = isDirty && !disabled && !saving

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '0.5px solid #f0f0f0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
        <p style={{ fontSize: 11, color: '#999', margin: 0 }}>{t('inbox.notes')}</p>
      </div>

      <textarea
        value={draft}
        onChange={e => setDraft(e.target.value)}
        disabled={disabled || saving}
        placeholder={t('inbox.notesPlaceholder')}
        rows={5}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          resize: 'vertical',
          minHeight: 92,
          maxHeight: 180,
          border: '0.5px solid #e0e3f0',
          borderRadius: 8,
          background: disabled ? '#f9fafb' : 'white',
          color: '#111',
          lineHeight: 1.5,
          outline: 'none',
          padding: '8px 10px',
          opacity: disabled ? 0.65 : 1,
        }}
      />

      {isDirty && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 8 }}>
          <button
            type="button"
            onClick={() => setDraft(original)}
            disabled={saving}
            style={{
              fontSize: 11,
              padding: '4px 9px',
              border: '0.5px solid #e0e3f0',
              borderRadius: 6,
              background: 'white',
              color: '#666',
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {t('contacts.cancel')}
          </button>
          <button
            type="button"
            onClick={() => canSave && onSave?.(draft.trim())}
            disabled={!canSave}
            style={{
              fontSize: 11,
              padding: '4px 10px',
              border: 'none',
              borderRadius: 6,
              background: '#1535c8',
              color: 'white',
              fontWeight: 600,
              cursor: canSave ? 'pointer' : 'not-allowed',
              opacity: canSave ? 1 : 0.55,
            }}
          >
            {saving ? t('contacts.saving') : t('contacts.save')}
          </button>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────
// Desktop sub-components
// ─────────────────────────────────────────────

function avatarColors(channel) {
  const key = normalizeChannelKey(channel)
  if (key === 'zalo') return { bg: '#dbeafe', color: '#1d4ed8' }
  if (key === 'facebook') return { bg: '#ede9fe', color: '#7c3aed' }
  if (key === 'web') return { bg: '#ecfdf5', color: '#065f46' }
  if (key === 'whatsapp') return { bg: '#dcfce7', color: '#166534' }
  return { bg: '#f0f0f0', color: '#666' }
}

function ConversationAvatar({ channel, channels, name, avatarUrl, size = 36, iconSize = 16 }) {
  const iconChannels = channels || channel
  const primaryChannel = Array.isArray(iconChannels) ? iconChannels[0] : iconChannels
  const ac = avatarColors(primaryChannel)
  const [imgFailed, setImgFailed] = useState(false)
  const showImage = Boolean(avatarUrl) && !imgFailed
  return (
    <span style={{ display: 'inline-flex', alignItems: 'flex-end', flexShrink: 0, position: 'relative', width: size, height: size }}>
      {showImage ? (
        <img
          src={avatarUrl}
          alt=""
          onError={() => setImgFailed(true)}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            objectFit: 'cover',
            background: ac.bg,
            flexShrink: 0,
          }}
        />
      ) : (
        <span
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: ac.bg,
            color: ac.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: Math.round(size * 0.36),
            fontWeight: 600,
          }}
        >
          {(name || '?').charAt(0).toUpperCase()}
        </span>
      )}
      {iconChannels && (
        <span style={{ position: 'absolute', right: -3, bottom: -2, lineHeight: 0 }}>
          <ChannelIconStack channels={iconChannels} size={iconSize} overlap={5} />
        </span>
      )}
    </span>
  )
}

// ─────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────

export default function InboxPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { tenantId: storeTenantId, role } = useManagerStore()
  const branchBootstrapStatus = useAuthStore((s) => s.branchBootstrapStatus)
  const tenantId = storeTenantId
  const queryClient = useQueryClient()
  // Track B (realtime-inbox migration) — the session-level WS connection,
  // established once at app-shell mount (LangLayout.jsx) and shared across
  // every page via context; InboxPage only consumes it, never owns it.
  const {
    subscribeConversation,
    unsubscribeConversation,
    subscribeConversationList,
    unsubscribeConversationList,
  } = useWsSession()
  const inboxUi = useInboxUiStore((state) => state.byTenant[tenantId] || DEFAULT_INBOX_UI_STATE)
  const patchInboxUi = useInboxUiStore((state) => state.patchTenant)
  const {
    searchQuery, channelFilter, listFilter, staffFilter,
    selectedContactId, selectedChannel,
    mobileView, mobileContactId, mobileSelectedChannel,
  } = inboxUi
  const setSearchQuery = useCallback((value) => patchInboxUi(tenantId, { searchQuery: value }), [patchInboxUi, tenantId])
  const setChannelFilter = useCallback((value) => patchInboxUi(tenantId, { channelFilter: value }), [patchInboxUi, tenantId])
  const setListFilter = useCallback((value) => patchInboxUi(tenantId, { listFilter: value }), [patchInboxUi, tenantId])
  const setStaffFilter = useCallback((value) => patchInboxUi(tenantId, { staffFilter: value }), [patchInboxUi, tenantId])

  const channelOptions = useMemo(() => [
    { label: t('inbox.channel.all'),       value: 'all' },
    { label: t('inbox.channel.zalo'),   value: 'zalo' },
    { label: t('inbox.channel.zalo_zbs'), value: 'zalo_zbs' },
    { label: t('inbox.channel.facebook'), value: 'facebook' },
    { label: t('inbox.channel.web'),  value: 'web' },
  ], [t])

  const filterChips = useMemo(() => [
    { label: t('inbox.chipAll'), value: 'all' },
    { label: t('inbox.chipUnread'), value: 'unread' },
  ], [t])

  const desktopChannelTabs = useMemo(() => [
    { label: t('inbox.channel.all'),       value: 'all' },
    { label: t('inbox.channel.zalo'),   value: 'zalo' },
    { label: t('inbox.channel.zalo_zbs'), value: 'zalo_zbs' },
    { label: t('inbox.channel.facebook'), value: 'facebook' },
    { label: t('inbox.channel.web'),  value: 'web' },
  ], [t])

  // ── Shared state ──
  // ── Mobile-only state ──
  const [showChannelSheet, setShowChannelSheet] = useState(false)
  const [staffSectionOpen, setStaffSectionOpen] = useState(false)

  // ── Desktop-only state ──
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024)
  const [replyText, setReplyText]             = useState('')
  const [sending, setSending]                 = useState(false)
  const [sendError, setSendError]             = useState(null)
  const [uploadingImage, setUploadingImage]   = useState(false)
  const [uploadingFile, setUploadingFile]     = useState(false)
  const [assigningStaff, setAssigningStaff]   = useState(false)
  const [mobileReplyText, setMobileReplyText] = useState('')
  const [mobileSending, setMobileSending]     = useState(false)
  const [mobileUploadingImage, setMobileUploadingImage] = useState(false)
  const [mobileUploadingFile, setMobileUploadingFile] = useState(false)
  const [notesSavingContactId, setNotesSavingContactId] = useState(null)
  const [editContactModalOpen, setEditContactModalOpen] = useState(false)

  const messagesEndRef = useRef(null)
  const mobileMessagesEndRef = useRef(null)
  const messagesContainerRef = useRef(null)
  const mobileMessagesContainerRef = useRef(null)
  const desktopIsNearBottomRef = useRef(true)
  const mobileIsNearBottomRef = useRef(true)
  const shouldAutoScrollDesktopRef = useRef(false)
  const shouldAutoScrollMobileRef = useRef(false)
  const lastDesktopMessageCountRef = useRef(0)
  const lastMobileMessageCountRef = useRef(0)
  const desktopImageInputRef = useRef(null)
  const mobileImageInputRef = useRef(null)
  const desktopFileInputRef = useRef(null)
  const mobileFileInputRef = useRef(null)
  // B-FE7 (Track B4) — infinite-scroll sidebar. loadMoreSentinelRef is
  // rendered once per layout branch (desktop/mobile below), always the same
  // ref object since only one branch is ever mounted at a time.
  // sidebarScrollRootRef is only attached on the desktop branch (a
  // fixed-height overflow:auto div); left null on mobile, where
  // IntersectionObserver's default root (the page/AppShell viewport) is
  // already correct since that list scrolls with the page, not a nested
  // container.
  const loadMoreSentinelRef = useRef(null)
  const sidebarScrollRootRef = useRef(null)

  const updateNearBottom = useCallback((element, nearBottomRef) => {
    if (!element) return
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight
    nearBottomRef.current = distanceFromBottom <= CHAT_NEAR_BOTTOM_THRESHOLD
  }, [])

  const mapConversations = useCallback((data) => {
    // Conversation Service returns a paginated envelope; keep compatibility
    // with older array responses while mapping only the actual rows.
    const rows = Array.isArray(data) ? data : (data?.conversations || [])
    return toUnifiedContacts(rows, t).sort((a, b) => {
      const aTs = a.updated_at ? new Date(a.updated_at).getTime() : 0
      const bTs = b.updated_at ? new Date(b.updated_at).getTime() : 0
      return bTs - aTs
    })
  }, [t])

  // The backend excludes ZBS by default. Keep the existing local-only
  // filtering behavior for the other channel tabs, but opt into the ZBS
  // dataset explicitly so the dedicated tab can load conversations that are
  // intentionally absent from the default Inbox response.
  const serverChannel = normalizeChannelKey(channelFilter) === 'zalo_zbs' ? 'zalo_zbs' : undefined

  // B-FE7 (Track B4, pagination-and-ws-tenant-wide,
  // growchat-doc/realtime-inbox/spec-inbox-conversation-list-pagination.md)
  // — cursor-paginated infinite scroll. Each page's queryFn call maps its
  // own raw rows through mapConversations() (unchanged, still
  // toUnifiedContacts()+sort) — a page's ~24 raw API rows may unify into
  // fewer rows if a contact has multiple channels in that page (spec's Edge
  // Cases explicitly accepts this as normal). Known, accepted limitation:
  // grouping happens per-page, not across the whole loaded set, so a
  // multi-channel contact whose channels' `updated_at` values are far
  // enough apart to land on two different pages will briefly render as two
  // separate sidebar rows instead of one — the same class of pre-grouping
  // page-boundary tradeoff the spec already accepts for the "<24 rows
  // shown" case, just the two-page flavor of it. Not attempting a
  // cross-page re-group here; out of proportion for what this ticket asks.
  const conversationsQuery = useInfiniteQuery({
    queryKey: inboxKeys.conversationList(tenantId, staffFilter, serverChannel),
    queryFn: async ({ pageParam }) => {
      const params = {
        ...(staffFilter === 'all' ? {} : { assigned_staff_id: staffFilter }),
        ...(pageParam ? { cursor: pageParam } : {}),
        ...(serverChannel ? { channel: serverChannel } : {}),
      }
      const { conversations: rawRows, nextCursor } = await listConversations(tenantId, params)
      return { conversations: mapConversations(rawRows), nextCursor }
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
    enabled: Boolean(tenantId),
  })
  // Concatenation-order note: each page is already sorted updated_at desc
  // internally, and page N+1's rows are all older than page N's (that's
  // what the cursor guarantees) — so flattening pages in order preserves
  // global sort order without re-sorting here. The one place order could
  // otherwise drift (a WS upsert reshuffling rows) is handled by
  // patchConversationCaches() below collapsing to a single, already-resorted
  // page whenever that happens — so by the time this memo runs, there's
  // nothing left to fix up.
  const conversations = useMemo(
    () => dedupeConversationsById((conversationsQuery.data?.pages || []).flatMap((page) => page?.conversations || [])),
    [conversationsQuery.data]
  )
  // B-FE8 — dedupeConversationsById() above already guards the primary
  // append-a-new-page race the spec calls out (a WS-upserted row landing at
  // the front, then re-appearing in a later-fetched page) at render time,
  // for every render, not just right after fetchNextPage() resolves.
  //
  // patchConversationCaches() is the single write path every local
  // conversation-list mutation in this file goes through (mark-read,
  // assignment, AI toggle, the two WS merge handlers above, ...). Its
  // contract is unchanged from before B4 — callers still receive/return a
  // flat array of unified rows — but its cache is now
  // useInfiniteQuery's `{pages, pageParams}` instead of a flat array
  // underneath, so this flattens the pages in for the updater and collapses
  // the result back into a single page afterwards: page boundaries stop
  // meaning anything once a WS upsert can insert/reorder rows across them
  // anyway, and collapsing preserves the still-real `nextCursor` from
  // whatever was the last page before the collapse, so "load more" keeps
  // working. One consequence worth calling out: since ~every
  // conversation_list.upsert event runs through here, pages collapse back
  // to one almost continuously in an active tenant — which means the
  // WS_RESYNC_EVENT_TYPE handler's conversationsQuery.refetch() further
  // down (react-query replays each currently-recorded page's pageParam on
  // an infinite-query refetch) will, in practice, usually just refetch page
  // 1. That's an extension of this file's existing resync philosophy, not a
  // new regression — the code below already documents that resync fully
  // overwrites cached data with no incremental merge, same idea, now also
  // resetting how deep the user had scrolled. Accepted tradeoff, not
  // attempting to preserve scroll depth across a reconnect.
  const patchConversationCaches = useCallback((updater) => {
    queryClient.setQueriesData(
      { queryKey: inboxKeys.conversations(tenantId) },
      (current) => {
        if (!current || !Array.isArray(current.pages)) return current
        const flat = dedupeConversationsById(current.pages.flatMap((page) => page?.conversations || []))
        const updated = updater(flat)
        if (!Array.isArray(updated)) return current
        const lastPage = current.pages[current.pages.length - 1]
        const collapsedPage = { conversations: dedupeConversationsById(updated), nextCursor: lastPage?.nextCursor ?? null }
        return { pages: [collapsedPage], pageParams: [current.pageParams?.[0]] }
      }
    )
  }, [queryClient, tenantId])

  // B-FE7 — trigger "load more" via IntersectionObserver on a sentinel
  // element at the end of the sidebar list, rather than a scroll-position
  // listener or a manual button: it's the option that needed the least new
  // wiring on top of what the desktop/mobile sidebar JSX already renders,
  // doesn't run on every scroll-frame, and naturally does nothing once the
  // sentinel is unmounted (hasNextPage false — see the JSX below, B-FE9).
  // rootMargin gives it a head start before the sentinel is literally on
  // screen so the next page is usually ready before the user hits bottom.
  // hasNextPage/isFetchingNextPage are effect deps (not read inside a
  // stable callback) so a fresh observer — and fresh closure over the
  // current fetchNextPage — is created each time either flips, guarding
  // against firing a duplicate fetch while one is already in flight.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = conversationsQuery
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined
    const sentinel = loadMoreSentinelRef.current
    if (!sentinel) return undefined
    if (!hasNextPage || isFetchingNextPage) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          fetchNextPage()
        }
      },
      { root: sidebarScrollRootRef.current, rootMargin: '160px', threshold: 0 }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isDesktop])

  const staffQuery = useQuery({
    queryKey: inboxKeys.staff(tenantId),
    queryFn: () => listConversationStaff(tenantId),
    enabled: Boolean(tenantId),
    staleTime: 5 * 60_000,
  })
  const staffList = staffQuery.data || []
  const staffLoading = staffQuery.isPending

  const selectedConv = useMemo(
    () => conversations.find((conversation) => conversation.contact_id === selectedContactId) || null,
    [conversations, selectedContactId]
  )
  const mobileConv = useMemo(
    () => conversations.find((conversation) => conversation.contact_id === mobileContactId) || null,
    [conversations, mobileContactId]
  )
  const activeContactId = isDesktop ? selectedContactId : mobileContactId
  const activeChannel = isDesktop ? selectedChannel : mobileSelectedChannel
  const activeConv = isDesktop ? selectedConv : mobileConv
  const activeMeta = useMemo(() => getChannelMeta(activeConv, activeChannel), [activeConv, activeChannel])

  // Phase 2 cutover (growchat-doc/realtime-inbox/
  // spec-websocket-cutover-remove-polling.md) — no more polling here, in any
  // form. Live updates for the open thread arrive via the
  // `conversation.message` WS push (useWsEvent below), and the gap left by
  // Redis pub/sub being fire-and-forget is covered by the one-shot
  // resync-on-reconnect: see the WS_RESYNC_EVENT_TYPE handler further down,
  // which refetches this query (via detailQuery.refetch()) whenever the WS
  // session reconnects while a thread is open.
  const detailQuery = useQuery({
    queryKey: inboxKeys.detail(tenantId, activeContactId, activeChannel),
    queryFn: () => getContactMessages(tenantId, activeContactId, { channel: activeChannel, limit: 100 }),
    enabled: Boolean(tenantId && activeContactId && activeChannel),
  })

  // FE1.3/FE1.4 — subscribe the WS `conversation` topic to whichever
  // thread is open. activeContactId/activeChannel above already merge
  // desktop selection and mobile navigation via the isDesktop ternary, so
  // this one effect covers both surfaces. FE1.5 — switching threads runs
  // this effect's cleanup (unsubscribe) before the next run's subscribe;
  // wsClient.js's subscribeConversation() also unsubscribes any previous
  // pending thread itself as a second guard. Unmounting InboxPage (SPA nav
  // away) also unsubscribes, since the WS session itself lives above this
  // component (LangLayout) and stays connected.
  useEffect(() => {
    if (!activeContactId || !activeChannel) return undefined
    subscribeConversation(activeContactId, activeChannel)
    return () => unsubscribeConversation()
  }, [activeContactId, activeChannel, subscribeConversation, unsubscribeConversation])

  // B-FE3 (Track B2, pagination-and-ws-tenant-wide) — subscribe the new
  // tenant-wide conversation_list topic for the whole page's mount/unmount
  // lifecycle, deliberately NOT keyed off activeContactId/activeChannel
  // like the per-thread effect above: this topic isn't about any one
  // thread, it fans out a `conversation_list.upsert` event for every
  // message written anywhere in the tenant so the sidebar can insert/resort
  // conversations this tab never opened. tenantId is in the deps (not an
  // empty array) because WsSessionContext tears down and recreates its
  // client on tenant/branch switch (see that file's own effect) — a stale
  // subscribe() call would target a client instance that's already gone;
  // re-running this effect on tenantId change re-issues it against
  // whichever client is current. subscribeConversationList()/
  // unsubscribeConversationList() are themselves idempotent no-ops when
  // called redundantly (see wsClient.js).
  useEffect(() => {
    if (!tenantId) return undefined
    subscribeConversationList()
    return () => unsubscribeConversationList()
  }, [tenantId, subscribeConversationList, unsubscribeConversationList])

  // A handoff began or ended somewhere in the tenant (the bot triggered one,
  // another staff member resumed it, or the auto-resume timeout fired).
  // Handoff state lives on the list rows, so refetch them — rare enough that
  // a refetch is simpler than patching every cached page.
  useWsEvent('conversation.handoff', (event) => {
    if (!tenantId || event?.tenant_id !== tenantId) return
    queryClient.invalidateQueries({ queryKey: inboxKeys.conversations(tenantId) })
  })

  // FE1.3/FE1.4 — merge conversation.message pushes into the open thread's
  // detailQuery cache and into the conversation-list cache (preview/sort
  // hint). Only applied when the event matches the thread currently open —
  // the WS session is only ever subscribed to one conversation topic at a
  // time, so in practice this guard should never actually filter anything
  // out, but it's cheap insurance against an event for a thread this tab
  // just navigated away from arriving after the unsubscribe was sent.
  useWsEvent('conversation.message', (event) => {
    if (!event || event.contact_id !== activeContactId) return
    if (normalizeChannelKey(event.channel) !== normalizeChannelKey(activeChannel)) return
    const key = inboxKeys.detail(tenantId, activeContactId, activeChannel)
    queryClient.setQueryData(key, (current) => mergeMessageIntoDetailCache(current, event))
    patchConversationCaches((prev) => applyWsMessageToConversationList(prev, event, { t, activeContactId, activeChannel }))
  })

  // B-FE5 (Track B2, pagination-and-ws-tenant-wide) — tenant-wide
  // conversation_list.upsert: unlike conversation.message above, this one
  // is NOT filtered to the thread currently open — it's meant to patch/
  // insert ANY conversation in the tenant, including ones this tab never
  // loaded. Routed through applyConversationListUpsert() (see
  // inboxConversationListMerge.js), which patches-and-resorts an existing
  // row or inserts-and-resorts a brand-new one.
  useWsEvent('conversation_list.upsert', (event) => {
    patchConversationCaches((prev) => applyConversationListUpsert(prev, event, { t, activeContactId, activeChannel }))
  })

  // Phase 2 cutover — resync-on-reconnect (A-FE6/A-FE7). wsClient.js fires
  // WS_RESYNC_EVENT_TYPE exactly once every time the WS session's onopen
  // fires (first connect and every reconnect after — see wsClient.js), to
  // cover whatever Redis pub/sub events were fired-and-forgotten while this
  // tab was disconnected (or while the backend's publish itself failed —
  // append_message() writes DB before publishing, so refetching from REST
  // repairs both cases). Only runs while InboxPage is mounted, since
  // useWsEvent unsubscribes on unmount. Both refetches fully overwrite their
  // query's cached data (react-query's default `data` replacement — no
  // merge/append) and fail silently on network/5xx errors, same as
  // fetchNotifications() in AppShell.jsx/DesktopShell.jsx: on failure the
  // old state is left as-is and the next reconnect will resync again.
  useWsEvent(WS_RESYNC_EVENT_TYPE, () => {
    if (tenantId) {
      conversationsQuery.refetch().catch(() => {})
    }
    if (tenantId && activeContactId && activeChannel) {
      detailQuery.refetch().catch(() => {})
    }
  })
  const activeDetail = detailQuery.data ? {
    ...detailQuery.data,
    slots: detailQuery.data.slots || activeConv?.slots || {},
    updated_at: activeConv?.updated_at,
    stage: activeMeta?.stage ?? activeConv?.stage,
    booking_id: activeMeta?.booking_id ?? activeConv?.booking_id,
    channel_bot_enabled: detailQuery.data.channel_bot_enabled ?? activeMeta?.channel_bot_enabled ?? true,
    ai_toggle_disabled: detailQuery.data.ai_toggle_disabled ?? activeMeta?.ai_toggle_disabled ?? false,
    // The thread endpoint doesn't carry handoff state — it comes from the
    // conversation list row, kept live by the conversation.handoff event.
    handed_off: Boolean(activeMeta?.handed_off),
    ai_enabled: activeMeta?.mode !== 'staff',
  } : null
  const convDetail = isDesktop ? activeDetail : null
  const mobileDetail = isDesktop ? null : activeDetail
  const detailLoading = isDesktop && detailQuery.isPending
  const mobileDetailLoading = !isDesktop && detailQuery.isPending
  const appendDetailMessage = useCallback((contactId, channel, message) => {
    const key = inboxKeys.detail(tenantId, contactId, channel)
    const previous = queryClient.getQueryData(key)
    queryClient.setQueryData(key, (current) => current
      ? { ...current, messages: [...(current.messages || []), message] }
      : { messages: [message] })
    return () => queryClient.setQueryData(key, previous)
  }, [queryClient, tenantId])

  // Appends N messages in ONE setQueryData, so a batch costs one render instead
  // of N, and produces one rollback instead of N nested closures that each
  // snapshotted the cache at a different moment (calling those out of order
  // restores a mixed state).
  //
  // The rollback removes by _localId rather than restoring a whole snapshot. A
  // snapshot restore would also discard anything that arrived while the
  // optimistic append was in flight, such as an unrelated WS push, and — the
  // reason it matters most for a batch — one failed image would roll back every
  // sibling image that succeeded alongside it.
  const appendDetailMessages = useCallback((contactId, channel, messages) => {
    const key = inboxKeys.detail(tenantId, contactId, channel)
    queryClient.setQueryData(key, (current) => current
      ? { ...current, messages: [...(current.messages || []), ...messages] }
      : { messages: [...messages] })
    const localIds = messages.map(m => m._localId).filter(Boolean)
    return () => queryClient.setQueryData(key, (current) => current
      ? { ...current, messages: (current.messages || []).filter(m => !localIds.includes(m._localId)) }
      : current)
  }, [queryClient, tenantId])

  // Patches one message, leaving its siblings untouched — for a batch-send
  // response updating a single message's _status from 'sending' to 'sent' or
  // 'failed'. A patch and not a rollback: an image the customer has genuinely
  // received must stay put in the thread, and only the failed one changes how
  // it displays.
  //
  // Matches `_localId` or `message_id`, because a retry can be fired from
  // either shape of message. An optimistic row has only `_localId`; once
  // refreshThreadAndList has replaced it with the server's record, the only
  // identifier left is `message_id`. Matching one alone would make a retry on
  // the other silently patch nothing, so the request would fire with no
  // spinner and no visible result.
  const patchDetailMessage = useCallback((contactId, channel, messageRef, patch) => {
    if (!messageRef) return
    const key = inboxKeys.detail(tenantId, contactId, channel)
    queryClient.setQueryData(key, (current) => {
      if (!current) return current
      return {
        ...current,
        messages: (current.messages || []).map(m =>
          (m._localId && m._localId === messageRef) || (m.message_id && m.message_id === messageRef)
            ? { ...m, ...patch }
            : m
        ),
      }
    })
  }, [queryClient, tenantId])

  const refreshThreadAndList = useCallback((contactId, channel) => Promise.all([
    queryClient.invalidateQueries({ queryKey: inboxKeys.detail(tenantId, contactId, channel) }),
    queryClient.invalidateQueries({ queryKey: inboxKeys.conversations(tenantId) }),
  ]), [queryClient, tenantId])

  const selectedChannelMeta = useMemo(
    () => getChannelMeta(selectedConv, selectedChannel),
    [selectedConv, selectedChannel]
  )
  const selectedConversationUserId = selectedChannelMeta?.user_id || null
  const selectedConversationId = selectedChannelMeta?.conversation_id || null
  const mobileSelectedMeta = useMemo(
    () => getChannelMeta(mobileConv, mobileSelectedChannel),
    [mobileConv, mobileSelectedChannel]
  )
  const mobileSelectedUserId = mobileSelectedMeta?.user_id || null
  const selectedIsZbs = normalizeChannelKey(selectedChannel) === 'zalo_zbs'
  // Same gate as the backend's CONVERSATIONS_REPLY: view-only roles get no suggestion button.
  // Waits for the profile/branch role (the store role defaults to 'admin' before it loads).
  const canReply = canStaffReply(role, branchBootstrapStatus)
  const mobileSelectedIsZbs = normalizeChannelKey(mobileSelectedChannel) === 'zalo_zbs'
  const aiEnabled = selectedChannelMeta?.mode !== 'staff'
  const mobileAiEnabled = mobileSelectedMeta?.mode !== 'staff'
  const selectedAiToggleDisabled = Boolean(convDetail?.ai_toggle_disabled ?? selectedChannelMeta?.ai_toggle_disabled)
  const mobileAiToggleDisabled = Boolean(mobileDetail?.ai_toggle_disabled ?? mobileSelectedMeta?.ai_toggle_disabled)
  const selectedHandedOff = Boolean(convDetail?.handed_off ?? selectedChannelMeta?.handed_off)
  const mobileHandedOff = Boolean(mobileDetail?.handed_off ?? mobileSelectedMeta?.handed_off)

  // Desktop and mobile each get their own tray, mirroring how the existing
  // reply text, upload flags and file inputs are already split per surface.
  // One shared instance would let a half-built selection follow the user
  // across a resize into a different conversation's composer.
  const onBatchSendError = useCallback((err) => {
    Sentry.captureException(err, { tags: { action: 'send_images_batch' }, extra: { tenantId } })
    if (!showZaloOASubscriptionToast(err, t)) toast.error(t('inbox.batchSendFailed'))
  }, [t, tenantId])

  const imageBatchDesktop = useImageBatchSend({
    tenantId,
    contactId: selectedContactId,
    channel: normalizeChannelKey(selectedChannel),
    userId: selectedConversationUserId,
    appendDetailMessages,
    patchDetailMessage,
    refreshThreadAndList,
    sendImages: sendConversationImages,
    uploadImage: uploadConversationImage,
    onError: onBatchSendError,
  })

  const imageBatchMobile = useImageBatchSend({
    tenantId,
    contactId: mobileConv?.contact_id || null,
    channel: normalizeChannelKey(mobileSelectedChannel),
    userId: mobileSelectedUserId,
    appendDetailMessages,
    patchDetailMessage,
    refreshThreadAndList,
    sendImages: sendConversationImages,
    uploadImage: uploadConversationImage,
    onError: onBatchSendError,
  })

  // Picking images never sends: it fills the tray, which is phase A. Files
  // turned away by the batch cap are reported rather than dropped silently.
  const handleBatchPick = useCallback((batch, event) => {
    const files = event.target.files
    if (files?.length) {
      const { rejectedForCap } = batch.add(files)
      if (rejectedForCap > 0) {
        toast.warn(t('inbox.batchCapReached', { max: batch.maxImages, dropped: rejectedForCap }))
      }
    }
    // Reset so re-picking the same file fires onChange again.
    event.target.value = ''
  }, [t])

  useEffect(() => {
    const h = () => setIsDesktop(window.innerWidth >= 1024)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [])

  // ── Reconcile selection whenever conversationsQuery's data changes ──
  // (mount fetch, mutations, or the resync-on-reconnect refetch above — no
  // polling drives this anymore since Phase 2 cutover).
  useEffect(() => {
    if (!conversationsQuery.isSuccess) return
    const reconcile = (contactId, channel, mobile = false) => {
      if (!contactId) return
      const conversation = conversations.find((item) => item.contact_id === contactId)
      if (!conversation) {
        patchInboxUi(tenantId, mobile
          ? { mobileContactId: null, mobileSelectedChannel: null, mobileView: 'list' }
          : { selectedContactId: null, selectedChannel: null })
      } else if (!conversation.channels.includes(channel)) {
        const nextChannel = conversation.activeChannel || conversation.channels[0] || null
        patchInboxUi(tenantId, mobile ? { mobileSelectedChannel: nextChannel } : { selectedChannel: nextChannel })
      }
    }
    reconcile(selectedContactId, selectedChannel)
    reconcile(mobileContactId, mobileSelectedChannel, true)
  }, [conversations, conversationsQuery.isSuccess, mobileContactId, mobileSelectedChannel, patchInboxUi, selectedChannel, selectedContactId, tenantId])

  useEffect(() => {
    shouldAutoScrollDesktopRef.current = Boolean(selectedContactId && selectedChannel)
    desktopIsNearBottomRef.current = true
    lastDesktopMessageCountRef.current = 0
    setReplyText('')
  }, [selectedContactId, selectedChannel])

  useEffect(() => {
    shouldAutoScrollMobileRef.current = Boolean(mobileConv?.contact_id && mobileSelectedChannel)
    mobileIsNearBottomRef.current = true
    lastMobileMessageCountRef.current = 0
    setMobileReplyText('')
  }, [mobileConv?.contact_id, mobileSelectedChannel])

  // ── Scroll to bottom only on first load of each opened thread ──
  useEffect(() => {
    if (!shouldAutoScrollDesktopRef.current) return
    if (!convDetail || detailLoading) return
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
    desktopIsNearBottomRef.current = true
    shouldAutoScrollDesktopRef.current = false
  }, [convDetail, detailLoading])

  useEffect(() => {
    if (!shouldAutoScrollMobileRef.current) return
    if (!mobileDetail || mobileDetailLoading) return
    mobileMessagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
    mobileIsNearBottomRef.current = true
    shouldAutoScrollMobileRef.current = false
  }, [mobileDetail, mobileDetailLoading])

  useEffect(() => {
    const messages = convDetail?.messages || []
    const prevCount = lastDesktopMessageCountRef.current
    const nextCount = messages.length
    if (nextCount > prevCount) {
      const lastMessage = messages[nextCount - 1]
      if (lastMessage?.role === 'staff' || desktopIsNearBottomRef.current) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }
    }
    lastDesktopMessageCountRef.current = nextCount
  }, [convDetail?.messages])

  useEffect(() => {
    const messages = mobileDetail?.messages || []
    const prevCount = lastMobileMessageCountRef.current
    const nextCount = messages.length
    if (nextCount > prevCount) {
      const lastMessage = messages[nextCount - 1]
      if (lastMessage?.role === 'staff' || mobileIsNearBottomRef.current) {
        mobileMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }
    }
    lastMobileMessageCountRef.current = nextCount
  }, [mobileDetail?.messages])

  // ── Auto-select conversation from ?user= or ?search= param ──
  const userParam = searchParams.get('user')
  const searchParam = searchParams.get('search')

  useEffect(() => {
    if (!userParam && !searchParam) return
    if (conversations.length === 0) return
    if (userParam) {
      const match = conversations.find(c =>
        c.contact_id === userParam || c.channelMeta?.some(meta => meta.user_id === userParam)
      )
      if (match) {
        const preferredChannel = match.channelMeta?.find(meta => meta.user_id === userParam)?.channel
          || match.activeChannel
          || match.channels[0]
          || null
        patchInboxUi(tenantId, { selectedContactId: match.contact_id, selectedChannel: preferredChannel })
      }
    } else if (searchParam) {
      const match = conversations.find(c => conversationMatchesSearch(c, searchParam))
      if (match) {
        patchInboxUi(tenantId, {
          selectedContactId: match.contact_id,
          selectedChannel: match.activeChannel || match.channels[0] || null,
        })
      }
    }
  }, [userParam, searchParam, conversations, patchInboxUi, tenantId])

  function applyReadState(contactId, channel) {
    patchConversationCaches(prev => prev.map(c =>
      c.contact_id === contactId ? markContactChannelRead(c, channel) : c
    ))
  }

  const markReadMutation = useMutation({
    mutationFn: ({ conversationId }) => markConversationRead(tenantId, conversationId),
    onMutate: async ({ contactId, channel }) => {
      await queryClient.cancelQueries({ queryKey: inboxKeys.conversations(tenantId) })
      const snapshots = queryClient.getQueriesData({ queryKey: inboxKeys.conversations(tenantId) })
      applyReadState(contactId, channel)
      return { snapshots }
    },
    onError: (err, variables, context) => {
      context?.snapshots?.forEach(([key, data]) => queryClient.setQueryData(key, data))
      Sentry.captureException(err, {
        tags: { action: 'mark_conversation_read' },
        extra: { tenantId, ...variables },
      })
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: inboxKeys.conversations(tenantId) }),
  })

  async function markSelectedConversationRead(conv, channel) {
    const meta = getChannelMeta(conv, channel)
    const conversationId = meta?.conversation_id
    if (!tenantId || !conv?.contact_id || !conversationId || Number(meta?.unread || 0) <= 0) return
    await markReadMutation.mutateAsync({ conversationId, contactId: conv.contact_id, channel }).catch(() => {})
  }

  // ── Load conversation detail ──
  async function loadConvDetail(conv, forcedChannel = null) {
    if (!tenantId) return
    const nextChannel = getReadableChannel(conv, forcedChannel || conv.activeChannel)
    if (!conv?.contact_id || !nextChannel) return
    shouldAutoScrollDesktopRef.current = true
    patchInboxUi(tenantId, { selectedContactId: conv.contact_id, selectedChannel: nextChannel })
    void markSelectedConversationRead(conv, nextChannel)
  }

  // ── Poll conversation detail every 5s while a contact + channel is selected ──
  // ── AI toggle (desktop) ──
  async function handleAIToggle() {
    if (!tenantId || !selectedConversationUserId || !selectedContactId || !selectedChannel) return
    if (selectedAiToggleDisabled) {
      toast.warn(t('inbox.channelBotDisabledTooltip', {
        defaultValue: 'Chatbot is disabled at channel level. Enable it in channel settings to use AI.',
      }))
      return
    }
    // The button shows whether the bot is actually replying, so a handoff
    // reads as "off" too: pressing it then resumes the handoff (and switches
    // AI back on if staff had also switched that off).
    const { resumeHandoff, setAiEnabled } = botToggleAction({ aiEnabled, handedOff: selectedHandedOff })
    try {
      if (resumeHandoff) await resumeHandoffForConversation(tenantId, selectedConversationUserId)
      if (setAiEnabled !== null) {
        const newEnabled = setAiEnabled
        await toggleAIForConversation(tenantId, selectedConversationUserId, newEnabled)
        patchConversationCaches(prev => prev.map(c =>
          c.contact_id !== selectedContactId
            ? c
            : {
                ...c,
                mode: normalizeChannelKey(selectedChannel) === normalizeChannelKey(c.activeChannel)
                  ? (newEnabled ? 'ai' : 'staff')
                  : c.mode,
                channelMeta: c.channelMeta.map(meta =>
                  normalizeChannelKey(meta.channel) === normalizeChannelKey(selectedChannel)
                    ? { ...meta, mode: newEnabled ? 'ai' : 'staff' }
                    : meta
                ),
              }
        ))
      }
      await refreshThreadAndList(selectedContactId, selectedChannel)
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: 'toggle_ai_desktop' },
        extra: { tenantId, userId: selectedConversationUserId, channel: selectedChannel },
      })
    }
  }

  // ── Mobile: open thread view ──
  async function openMobileThread(conv, forcedChannel = null) {
    if (!tenantId) return
    const nextChannel = getReadableChannel(conv, forcedChannel)
    if (!nextChannel) return
    shouldAutoScrollMobileRef.current = true
    patchInboxUi(tenantId, {
      mobileContactId: conv.contact_id,
      mobileSelectedChannel: nextChannel,
      mobileView: 'thread',
    })
    void markSelectedConversationRead(conv, nextChannel)
  }

  // ── Poll mobile thread detail every 5s while a contact thread is open ──
  async function handleMobileAIToggle() {
    if (!tenantId || !mobileSelectedUserId || !mobileConv?.contact_id || !mobileSelectedChannel) return
    if (mobileAiToggleDisabled) {
      toast.warn(t('inbox.channelBotDisabledTooltip', {
        defaultValue: 'Chatbot is disabled at channel level. Enable it in channel settings to use AI.',
      }))
      return
    }
    // The button shows whether the bot is actually replying, so a handoff
    // reads as "off" too: pressing it then resumes the handoff (and switches
    // AI back on if staff had also switched that off).
    const { resumeHandoff, setAiEnabled } = botToggleAction({ aiEnabled: mobileAiEnabled, handedOff: mobileHandedOff })
    try {
      if (resumeHandoff) await resumeHandoffForConversation(tenantId, mobileSelectedUserId)
      if (setAiEnabled !== null) {
        const newEnabled = setAiEnabled
        await toggleAIForConversation(tenantId, mobileSelectedUserId, newEnabled)
        patchConversationCaches(prev => prev.map(c =>
          c.contact_id !== mobileConv.contact_id
            ? c
            : {
                ...c,
                mode: normalizeChannelKey(mobileSelectedChannel) === normalizeChannelKey(c.activeChannel)
                  ? (newEnabled ? 'ai' : 'staff')
                  : c.mode,
                channelMeta: c.channelMeta.map(meta =>
                  normalizeChannelKey(meta.channel) === normalizeChannelKey(mobileSelectedChannel)
                    ? { ...meta, mode: newEnabled ? 'ai' : 'staff' }
                    : meta
                ),
              }
        ))
      }
      await refreshThreadAndList(mobileConv.contact_id, mobileSelectedChannel)
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: 'toggle_ai_mobile' },
        extra: { tenantId, userId: mobileSelectedUserId, channel: mobileSelectedChannel },
      })
    }
  }

  async function handleSaveContactNotes(contact, nextNotes) {
    if (!tenantId || !contact?.contact_id || String(contact.contact_id).startsWith('unknown:')) return
    const contactId = contact.contact_id
    setNotesSavingContactId(contactId)
    try {
      const updated = await patchContact(tenantId, contactId, {
        notes: nextNotes === '' ? null : nextNotes,
      })
      const savedNotes = updated?.notes ?? nextNotes
      patchConversationCaches(prev => prev.map(c =>
        c.contact_id === contactId ? { ...c, notes: savedNotes } : c
      ))
      await queryClient.invalidateQueries({ queryKey: inboxKeys.conversations(tenantId) })
      toast.success(t('inbox.notesSaved'))
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: 'patch_contact_notes' },
        extra: { tenantId, contactId },
      })
      toast.error(t('inbox.notesSaveFailed'))
    } finally {
      setNotesSavingContactId(current => (current === contactId ? null : current))
    }
  }

  async function handleSaveContactEdit(contact, patch) {
    if (!tenantId || !contact?.contact_id || String(contact.contact_id).startsWith('unknown:')) return
    const contactId = contact.contact_id
    try {
      const updated = await patchContact(tenantId, contactId, patch)
      patchConversationCaches(prev => prev.map(c =>
        c.contact_id === contactId ? { ...c, ...patch, ...(updated || {}) } : c
      ))
      await queryClient.invalidateQueries({ queryKey: inboxKeys.conversations(tenantId) })
      toast.success(t('inbox.contactSaved', { defaultValue: 'Customer info saved' }))
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: 'patch_contact_edit' },
        extra: { tenantId, contactId },
      })
      throw err
    }
  }

  async function handleAssignConversationStaff({ contactId, channel, conversationId, staffId }) {
    if (!tenantId || !contactId || !channel || !conversationId || assigningStaff) return
    setAssigningStaff(true)
    const normalizedStaffId = staffId || null
    const channelKey = normalizeChannelKey(channel)
    try {
      const assignment = await assignConversation(tenantId, conversationId, normalizedStaffId)
      const patchConversation = (contact) => {
        if (!contact || contact.contact_id !== contactId) return contact
        const channelMeta = (contact.channelMeta || []).map(meta =>
          normalizeChannelKey(meta.channel) === channelKey
            ? { ...meta, ...assignment }
            : meta
        )
        const activeMeta = channelMeta.find(meta => normalizeChannelKey(meta.channel) === normalizeChannelKey(contact.activeChannel))
        const selectedMeta = channelMeta.find(meta => normalizeChannelKey(meta.channel) === channelKey)
        const assignmentSource = activeMeta || selectedMeta || assignment
        return {
          ...contact,
          channelMeta,
          assigned_staff_id: assignmentSource?.assigned_staff_id || null,
          assigned_staff_name: assignmentSource?.assigned_staff_name || null,
          assigned_staff_email: assignmentSource?.assigned_staff_email || null,
          assigned_at: assignmentSource?.assigned_at || null,
        }
      }

      patchConversationCaches(prev => prev.map(patchConversation))
      await queryClient.invalidateQueries({ queryKey: inboxKeys.conversations(tenantId) })
      toast.success(normalizedStaffId ? t('inbox.assignmentSaved') : t('inbox.assignmentCleared'))
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: 'assign_conversation_staff' },
        extra: { tenantId, conversationId, contactId, channel, staffId: normalizedStaffId },
      })
      toast.error(t('inbox.assignmentFailed'))
    } finally {
      setAssigningStaff(false)
    }
  }

  async function handleAssignSelectedConversation(staffId) {
    return handleAssignConversationStaff({
      contactId: selectedContactId,
      channel: selectedChannel,
      conversationId: selectedConversationId,
      staffId,
    })
  }

  async function handleAssignMobileConversation(staffId) {
    return handleAssignConversationStaff({
      contactId: mobileConv?.contact_id,
      channel: mobileSelectedChannel,
      conversationId: mobileSelectedMeta?.conversation_id,
      staffId,
    })
  }

  // One send button drives both flows, so staff never have to pick between two
  // send affordances. An empty tray delegates to the original text-only
  // handler untouched; a filled tray sends the batch and uses whatever is
  // typed as the caption, which is why the button stays enabled on empty text
  // while images are waiting.
  //
  // A plain declaration, not useCallback: it is only ever handed to DOM
  // handlers, where a stable identity buys nothing, and it has to sit beside
  // the handlers it delegates to rather than above them.
  async function handleDesktopPrimarySend() {
    if (imageBatchDesktop.items.length === 0) {
      handleSend()
      return
    }
    if (imageBatchDesktop.isPreparing) {
      toast.info(t('inbox.batchWaitPreparing'))
      return
    }
    if (imageBatchDesktop.readyCount === 0) return
    const caption = replyText
    setReplyText('')
    const outcome = await imageBatchDesktop.send(caption)
    // Hand the caption back so a failed batch does not swallow the text.
    if (!outcome?.sent) setReplyText(caption)
  }

  async function handleMobilePrimarySend() {
    if (imageBatchMobile.items.length === 0) {
      handleMobileSend()
      return
    }
    if (imageBatchMobile.isPreparing) {
      toast.info(t('inbox.batchWaitPreparing'))
      return
    }
    if (imageBatchMobile.readyCount === 0) return
    const caption = mobileReplyText
    setMobileReplyText('')
    const outcome = await imageBatchMobile.send(caption)
    if (!outcome?.sent) setMobileReplyText(caption)
  }

  async function handleSend() {
    if (selectedIsZbs || !replyText.trim() || !selectedConversationUserId || sending || uploadingImage || uploadingFile || !tenantId) return
    setSending(true)
    setSendError(null)
    const text = replyText.trim()
    setReplyText('')
    const optimistic = { role: 'staff', content: text, timestamp: new Date().toISOString() }
    const rollback = appendDetailMessage(selectedContactId, selectedChannel, optimistic)
    try {
      const { sendMessage } = await import('../api/conversations')
      assertSendMessageSucceeded(await sendMessage(tenantId, selectedConversationUserId, text))
      await refreshThreadAndList(selectedContactId, selectedChannel)
    } catch (err) {
      rollback()
      Sentry.captureException(err, {
        tags: { action: 'send_message_desktop' },
        extra: { tenantId, userId: selectedConversationUserId },
      })
      const subscriptionMessage = getZaloOASubscriptionToastMessage(err, t)
      if (subscriptionMessage) {
        toast.error(subscriptionMessage, { toastId: 'zalo-oa-subscription-error' })
      } else {
        toast.error(t('inbox.sendFailed'))
      }
    } finally {
      setSending(false)
    }
  }

  function handlePickDesktopImage() {
    if (selectedIsZbs || !selectedConversationUserId || sending || uploadingImage || uploadingFile) return
    desktopImageInputRef.current?.click()
  }

  // Superseded by the batch tray: the image picker now routes to
  // useImageBatchSend, and a single image is just a batch of one. Kept
  // intact rather than deleted so the one-shot path can be restored by
  // re-pointing the input's onChange, without reconstructing it. Delete
  // once the batch flow has been exercised in production.
  // eslint-disable-next-line no-unused-vars
  async function handleDesktopImageChange(event) {
    if (selectedIsZbs) {
      event.target.value = ''
      return
    }
    const file = event.target.files?.[0]
    if (!file) return
    if (!file.type?.startsWith('image/')) {
      toast.error(t('inbox.invalidImageFile'))
      event.target.value = ''
      return
    }
    if (!tenantId || !selectedConversationUserId || sending || uploadingImage || uploadingFile) {
      event.target.value = ''
      return
    }
    setUploadingImage(true)
    setSendError(null)
    let rollback = null
    try {
      const uploadFile = await compressImageForUpload(file, MAX_ZALO_IMAGE_SIZE_BYTES)
      const { sendMessage, uploadConversationImage } = await import('../api/conversations')
      const uploadRes = await uploadConversationImage(tenantId, uploadFile)
      const imageUrl = uploadRes?.url
      if (!imageUrl) throw new Error('missing image url')
      const optimistic = { role: 'staff', content: '', image_url: imageUrl, timestamp: new Date().toISOString() }
      rollback = appendDetailMessage(selectedContactId, selectedChannel, optimistic)
      assertSendMessageSucceeded(await sendMessage(tenantId, selectedConversationUserId, '', null, imageUrl))
      await refreshThreadAndList(selectedContactId, selectedChannel)
    } catch (err) {
      rollback?.()
      if (err?.message === 'image_still_too_large') {
        toast.warn(t('inbox.imageTooLarge'))
      } else {
        Sentry.captureException(err, { tags: { action: 'upload_image_desktop' }, extra: { tenantId, userId: selectedConversationUserId } })
        if (!showZaloOASubscriptionToast(err, t)) {
          toast.error(t('inbox.imageUploadFailed'))
        }
      }
    } finally {
      setUploadingImage(false)
      event.target.value = ''
    }
  }

  function handlePickDesktopFile() {
    if (selectedIsZbs || !selectedConversationUserId || sending || uploadingImage || uploadingFile) return
    desktopFileInputRef.current?.click()
  }

  async function handleDesktopFileChange(event) {
    if (selectedIsZbs) {
      event.target.value = ''
      return
    }
    const file = event.target.files?.[0]
    if (!file) return
    if (file.type?.startsWith('image/')) {
      toast.info(t('inbox.useImageButton'))
      event.target.value = ''
      return
    }
    if (!tenantId || !selectedConversationUserId || sending || uploadingImage || uploadingFile) {
      event.target.value = ''
      return
    }
    setUploadingFile(true)
    setSendError(null)
    let rollback = null
    try {
      const { sendConversationFile, uploadConversationFile } = await import('../api/conversations')
      const uploadRes = await uploadConversationFile(tenantId, file)
      if (!uploadRes?.url) throw new Error('missing file url')
      const attachment = {
        kind: uploadRes.kind || 'file',
        url: uploadRes.url,
        download_url: uploadRes.download_url,
        filename: uploadRes.filename || file.name,
        mime_type: uploadRes.mime_type || file.type || undefined,
        size: uploadRes.size ?? file.size,
      }
      const optimistic = { role: 'staff', content: '', attachments: [attachment], timestamp: new Date().toISOString() }
      rollback = appendDetailMessage(selectedContactId, selectedChannel, optimistic)
      assertSendMessageSucceeded(await sendConversationFile(tenantId, selectedConversationUserId, attachment))
      await refreshThreadAndList(selectedContactId, selectedChannel)
    } catch (err) {
      rollback?.()
      Sentry.captureException(err, { tags: { action: 'upload_file_desktop' }, extra: { tenantId, userId: selectedConversationUserId, fileName: file.name } })
      if (!showZaloOASubscriptionToast(err, t)) toast.error(t('inbox.fileUploadFailed'))
    } finally {
      setUploadingFile(false)
      event.target.value = ''
    }
  }

  async function handleMobileSend() {
    if (mobileSelectedIsZbs || !mobileReplyText.trim() || mobileSending || mobileUploadingImage || mobileUploadingFile || !tenantId || !mobileSelectedUserId) return
    setMobileSending(true)
    const text = mobileReplyText.trim()
    setMobileReplyText('')
    const optimistic = { role: 'staff', content: text, timestamp: new Date().toISOString() }
    const rollback = appendDetailMessage(mobileConv.contact_id, mobileSelectedChannel, optimistic)
    try {
      const { sendMessage } = await import('../api/conversations')
      assertSendMessageSucceeded(await sendMessage(tenantId, mobileSelectedUserId, text))
      await refreshThreadAndList(mobileConv.contact_id, mobileSelectedChannel)
    } catch (err) {
      rollback()
      Sentry.captureException(err, {
        tags: { action: 'send_message_mobile' },
        extra: { tenantId, userId: mobileSelectedUserId },
      })
      showZaloOASubscriptionToast(err, t)
    }
    setMobileSending(false)
  }

  function handlePickMobileImage() {
    if (mobileSelectedIsZbs || !mobileSelectedUserId || mobileSending || mobileUploadingImage || mobileUploadingFile) return
    mobileImageInputRef.current?.click()
  }

  // Superseded by the batch tray: the image picker now routes to
  // useImageBatchSend, and a single image is just a batch of one. Kept
  // intact rather than deleted so the one-shot path can be restored by
  // re-pointing the input's onChange, without reconstructing it. Delete
  // once the batch flow has been exercised in production.
  // eslint-disable-next-line no-unused-vars
  async function handleMobileImageChange(event) {
    if (mobileSelectedIsZbs) {
      event.target.value = ''
      return
    }
    const file = event.target.files?.[0]
    if (!file) return
    if (!file.type?.startsWith('image/')) {
      toast.error(t('inbox.invalidImageFile'))
      event.target.value = ''
      return
    }
    if (!tenantId || !mobileSelectedUserId || mobileSending || mobileUploadingImage || mobileUploadingFile) {
      event.target.value = ''
      return
    }
    setMobileUploadingImage(true)
    let rollback = null
    try {
      const uploadFile = await compressImageForUpload(file, MAX_ZALO_IMAGE_SIZE_BYTES)
      const { sendMessage, uploadConversationImage } = await import('../api/conversations')
      const uploadRes = await uploadConversationImage(tenantId, uploadFile)
      const imageUrl = uploadRes?.url
      if (!imageUrl) throw new Error('missing image url')
      const optimistic = { role: 'staff', content: '', image_url: imageUrl, timestamp: new Date().toISOString() }
      rollback = appendDetailMessage(mobileConv.contact_id, mobileSelectedChannel, optimistic)
      assertSendMessageSucceeded(await sendMessage(tenantId, mobileSelectedUserId, '', null, imageUrl))
      await refreshThreadAndList(mobileConv.contact_id, mobileSelectedChannel)
    } catch (err) {
      rollback?.()
      if (err?.message === 'image_still_too_large') {
        toast.warn(t('inbox.imageTooLarge'))
      } else {
        Sentry.captureException(err, { tags: { action: 'upload_image_mobile' }, extra: { tenantId, userId: mobileSelectedUserId } })
        if (!showZaloOASubscriptionToast(err, t)) {
          toast.error(t('inbox.imageUploadFailed'))
        }
      }
    } finally {
      setMobileUploadingImage(false)
      event.target.value = ''
    }
  }

  function handlePickMobileFile() {
    if (mobileSelectedIsZbs || !mobileSelectedUserId || mobileSending || mobileUploadingImage || mobileUploadingFile) return
    mobileFileInputRef.current?.click()
  }

  async function handleMobileFileChange(event) {
    if (mobileSelectedIsZbs) {
      event.target.value = ''
      return
    }
    const file = event.target.files?.[0]
    if (!file) return
    if (file.type?.startsWith('image/')) {
      toast.info(t('inbox.useImageButton'))
      event.target.value = ''
      return
    }
    if (!tenantId || !mobileSelectedUserId || mobileSending || mobileUploadingImage || mobileUploadingFile) {
      event.target.value = ''
      return
    }
    setMobileUploadingFile(true)
    let rollback = null
    try {
      const { sendConversationFile, uploadConversationFile } = await import('../api/conversations')
      const uploadRes = await uploadConversationFile(tenantId, file)
      if (!uploadRes?.url) throw new Error('missing file url')
      const attachment = {
        kind: uploadRes.kind || 'file',
        url: uploadRes.url,
        download_url: uploadRes.download_url,
        filename: uploadRes.filename || file.name,
        mime_type: uploadRes.mime_type || file.type || undefined,
        size: uploadRes.size ?? file.size,
      }
      const optimistic = { role: 'staff', content: '', attachments: [attachment], timestamp: new Date().toISOString() }
      rollback = appendDetailMessage(mobileConv.contact_id, mobileSelectedChannel, optimistic)
      assertSendMessageSucceeded(await sendConversationFile(tenantId, mobileSelectedUserId, attachment))
      await refreshThreadAndList(mobileConv.contact_id, mobileSelectedChannel)
    } catch (err) {
      rollback?.()
      Sentry.captureException(err, { tags: { action: 'upload_file_mobile' }, extra: { tenantId, userId: mobileSelectedUserId, fileName: file.name } })
      if (!showZaloOASubscriptionToast(err, t)) toast.error(t('inbox.fileUploadFailed'))
    } finally {
      setMobileUploadingFile(false)
      event.target.value = ''
    }
  }

  // ── Filtered list (mobile uses local filter too) ──
  const filtered = useMemo(() => {
    const normalizedChannelFilter = normalizeChannelKey(channelFilter)
    return conversations.filter(c => {
      const matchSearch = conversationMatchesSearch(c, searchQuery)
      const matchChannel =
        normalizedChannelFilter === 'all' ||
        (c.channels || []).map(normalizeChannelKey).includes(normalizedChannelFilter)
      const matchListFilter = conversationMatchesListFilter(c, listFilter)
      return matchSearch && matchChannel && matchListFilter
    })
  }, [channelFilter, conversations, listFilter, searchQuery])

  const currentFilterLabel = channelOptions.find(o => o.value === channelFilter)?.label ?? t('inbox.channel.all')

  // ════════════════════════════════════════════
  // DESKTOP LAYOUT
  // ════════════════════════════════════════════

  if (isDesktop) {
    const conv  = selectedConv
    const msgs  = convDetail?.messages ?? []
    const slots = convDetail?.slots ?? conv?.slots ?? {}
    const basicInfoRows = buildCustomerBasicInfoRows(conv, convDetail, slots, t)
    const contactIdInvalid = !conv?.contact_id || String(conv.contact_id).startsWith('unknown:')
    const editContact = buildCustomerEditContact(conv, convDetail, slots)
    const selectedAssignment = getChannelMeta(conv, selectedChannel) || conv || {}
    const activeAssignedStaffId = selectedAssignment?.assigned_staff_id || ''
    const desktopAiBannerText = selectedAiToggleDisabled
      ? t('inbox.channelBotDisabledBanner', {
          defaultValue: 'Chatbot is disabled at channel level. Messages will still arrive for staff to handle manually.',
        })
      : t('inbox.disabledBanner')
    const desktopAiButtonLabel = selectedAiToggleDisabled
      ? t('inbox.channelChatbotDisabled', { defaultValue: 'Channel bot off' })
      : (isBotReplying({ aiEnabled, handedOff: selectedHandedOff }) ? t('inbox.disableChatbot') : t('inbox.enableChatbot'))

    return (
      <DesktopShell>
      <div style={{ display: 'flex', flexDirection: 'row', height: 'calc(100vh - 80px)', overflow: 'hidden' }}>

        {/* ── LEFT PANEL ── */}
        <div style={{ width: 300, flexShrink: 0, height: '100%', background: 'white', borderRight: '0.5px solid #e0e3f0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ padding: '16px 16px 0', borderBottom: '0.5px solid #e0e3f0', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
              <MessageSquareText size={16} color="#1535c8" />
              <span style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>{t('inbox.conversations')}</span>
            </div>
            {/* Channel tabs */}
            <HorizontalTabScroller ariaLabel={t('inbox.channelSheetTitle')}>
              {desktopChannelTabs.map(ch => (
                <button
                  key={ch.value}
                  role="tab"
                  aria-selected={channelFilter === ch.value}
                  onClick={() => setChannelFilter(ch.value)}
                  style={{ fontSize: 12, padding: '6px 10px', cursor: 'pointer', background: 'none', border: 'none', whiteSpace: 'nowrap', color: channelFilter === ch.value ? '#1535c8' : '#999', borderBottom: `2px solid ${channelFilter === ch.value ? '#1535c8' : 'transparent'}`, fontWeight: channelFilter === ch.value ? 500 : 400, transition: 'all 0.15s', flexShrink: 0 }}
                >
                  {ch.label}
                </button>
              ))}
            </HorizontalTabScroller>
          </div>

          {/* Search */}
          <div style={{ padding: '8px 12px', flexShrink: 0 }}>
            <div style={{ background: '#f9fafb', border: '0.5px solid #e0e3f0', borderRadius: 8, padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Search size={13} color="#9ca3af" />
              <input style={{ flex: 1, fontSize: 12, color: '#111', background: 'none', border: 'none', outline: 'none' }}
                placeholder={t('inbox.searchPlaceholder')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            </div>
          </div>

          {/* Filter chips */}
          <div style={{ padding: '0 12px 8px', display: 'flex', gap: 8, flexShrink: 0 }}>
            {filterChips.map(chip => {
              const active = listFilter === chip.value
              return (
                <button
                  key={chip.value}
                  onClick={() => setListFilter(current => current === chip.value || chip.value === 'all' ? 'all' : chip.value)}
                  style={{
                    fontSize: 11,
                    padding: '3px 10px',
                    border: `0.5px solid ${active ? '#1535c8' : '#e0e3f0'}`,
                    borderRadius: 6,
                    background: active ? '#e8ecff' : 'white',
                    cursor: 'pointer',
                    color: active ? '#1535c8' : '#666',
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  {chip.label}
                </button>
              )
            })}
          </div>

          {/* Staff assignment filters (collapsible, default closed) */}
          <div style={{ padding: '0 12px 10px', borderBottom: '0.5px solid #e0e3f0', flexShrink: 0 }}>
            <button
              onClick={() => setStaffSectionOpen(open => !open)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: staffSectionOpen ? 6 : 0, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#999', fontWeight: 600 }}>
                {t('inbox.staffSection')}{staffFilter !== 'all' ? ' •' : ''}
                <ChevronDown size={11} style={{ transform: staffSectionOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
              </span>
              <span style={{ fontSize: 10, color: '#999' }}>{staffLoading ? t('common.loading') : staffList.length}</span>
            </button>
            {staffSectionOpen && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 150, overflowY: 'auto' }}>
                <StaffFilterButton
                  active={staffFilter === 'all'}
                  label={t('inbox.allStaff')}
                  count={null}
                  onClick={() => setStaffFilter('all')}
                />
                <StaffFilterButton
                  active={staffFilter === UNASSIGNED_STAFF_FILTER}
                  label={t('inbox.unassigned')}
                  count={null}
                  onClick={() => setStaffFilter(UNASSIGNED_STAFF_FILTER)}
                />
                {staffList.map(staff => (
                  <StaffFilterButton
                    key={staff.id}
                    active={staffFilter === staff.id}
                    label={staffDisplayName(staff)}
                    count={staff.assigned_count || 0}
                    onClick={() => setStaffFilter(staff.id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Conversation list */}
          <div ref={sidebarScrollRootRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
            {conversationsQuery.isPending && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                <SpinnerRing size={28} color="#1535c8" />
              </div>
            )}
            {filtered.map(c => {
              const isActive = c.contact_id === selectedContactId
              const unreadCount = Number(c.unread || 0)
              return (
                <div key={c.id}
                  onClick={() => loadConvDetail(c)}
                  style={{ padding: '10px 14px', borderBottom: '0.5px solid #f0f0f0', cursor: 'pointer', display: 'flex', gap: 10, background: isActive ? '#e8ecff' : 'white', transition: 'background 0.1s' }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f5f7ff' }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'white' }}
                >
                  <ConversationAvatar channels={c.channels} name={c.name} avatarUrl={c.avatar_url} />
                  {/* Meta */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0, flex: 1 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                      </div>
                      <span style={{ fontSize: 10, color: '#999', flexShrink: 0, marginLeft: 4 }}>{c.time}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                      <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.preview}</div>
                      <UnreadCountBadge count={unreadCount} />
                    </div>
                    <div style={{ marginTop: 3, display: 'flex', gap: 4, alignItems: 'center' }}>
                      {c.mode !== 'ai' && (
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#f0fdf4', color: '#15803d' }}>
                          <ModePill mode={c.mode} t={t} />
                        </span>
                      )}
                      <HandoffPill handedOff={isContactHandedOff(c)} />
                      {c.assigned_staff_name && (
                        <span title={c.assigned_staff_email || c.assigned_staff_name} style={{ minWidth: 0, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#f3e8ff', color: '#7e22ce' }}>
                          {c.assigned_staff_name}
                        </span>
                      )}
                      <span style={{ marginLeft: 'auto' }}>
                        <AiBotStatusDot mode={c.mode} channelBotEnabled={c.channel_bot_enabled} handedOff={isContactHandedOff(c)} t={t} />
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
            {/* B-FE9 — the sentinel (and its spinner) only exist in the DOM
                while there's actually another page to fetch; once
                hasNextPage is false (Mongo-leg tenant from page 1, or the
                conversation-service leg genuinely ran out of pages), there's
                nothing here at all — no dead "load more" affordance. */}
            {conversationsQuery.hasNextPage && (
              <div ref={loadMoreSentinelRef} style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                {conversationsQuery.isFetchingNextPage && <SpinnerRing size={20} color="#1535c8" />}
              </div>
            )}
          </div>
        </div>

        {/* ── MIDDLE PANEL ── */}
        <div style={{ flex: 1, height: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f9fafb' }}>

          {!conv ? (
            /* Empty state */
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#e8ecff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <MessageSquareText size={28} color="#1535c8" />
              </div>
              <p style={{ fontSize: 16, fontWeight: 600, color: '#111', marginTop: 16 }}>{t('inbox.selectConversation')}</p>
              <p style={{ fontSize: 13, color: '#999', marginTop: 8, textAlign: 'center', maxWidth: 240 }}>{t('inbox.selectConversationHint')}</p>
            </div>
          ) : (
            <>
              {/* Chat header */}
              <div style={{ padding: '12px 18px', borderBottom: '0.5px solid #e0e3f0', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <ConversationAvatar channels={conv.channels} name={conv.name} avatarUrl={conv.avatar_url} />
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 500, color: '#111' }}>{conv.name}</p>
                    <div style={{ marginTop: 6 }}>
                      <ChannelToggle
                        channels={conv.channels}
                        selectedChannel={selectedChannel}
                        unreadByChannel={conv.unreadByChannel}
                        onSelect={ch => loadConvDetail(conv, ch)}
                        t={t}
                      />
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#666' }}>
                    <span style={{ whiteSpace: 'nowrap' }}>{t('inbox.assignStaff')}</span>
                    <select
                      value={activeAssignedStaffId}
                      disabled={!selectedConversationId || assigningStaff}
                      onChange={e => handleAssignSelectedConversation(e.target.value)}
                      style={{
                        minWidth: 150,
                        maxWidth: 190,
                        border: '0.5px solid #e0e3f0',
                        borderRadius: 8,
                        background: 'white',
                        color: '#111',
                        fontSize: 12,
                        padding: '6px 8px',
                        outline: 'none',
                        cursor: selectedConversationId && !assigningStaff ? 'pointer' : 'not-allowed',
                        opacity: selectedConversationId && !assigningStaff ? 1 : 0.6,
                      }}
                    >
                      <option value="">{t('inbox.unassigned')}</option>
                      {staffList.map(staff => (
                        <option key={staff.id} value={staff.id}>{staffDisplayName(staff)}</option>
                      ))}
                    </select>
                  </label>
                  <button
                    onClick={handleAIToggle}
                    disabled={selectedAiToggleDisabled || !selectedConversationUserId}
                    title={selectedAiToggleDisabled ? t('inbox.channelBotDisabledTooltip', { defaultValue: 'Chatbot is disabled at channel level. Enable it in channel settings to use AI.' }) : undefined}
                    style={{
                      fontSize: 12,
                      padding: '6px 14px',
                      borderRadius: 8,
                      border: 'none',
                      cursor: selectedAiToggleDisabled || !selectedConversationUserId ? 'not-allowed' : 'pointer',
                      fontWeight: 500,
                      background: selectedAiToggleDisabled ? '#94a3b8' : '#16a34a',
                      color: 'white',
                      whiteSpace: 'nowrap',
                      opacity: selectedAiToggleDisabled || !selectedConversationUserId ? 0.8 : 1,
                    }}
                  >
                    {desktopAiButtonLabel}
                  </button>
                </div>
              </div>

              {/* Disabled banner */}
              {(selectedAiToggleDisabled || !aiEnabled) && (
                <div style={{ background: selectedAiToggleDisabled ? '#fffbeb' : '#fef2f2', borderBottom: `0.5px solid ${selectedAiToggleDisabled ? '#fde68a' : '#fecaca'}`, padding: '8px 18px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <AlertCircle size={14} color={selectedAiToggleDisabled ? '#b45309' : '#ef4444'} />
                  <span style={{ fontSize: 12, color: selectedAiToggleDisabled ? '#92400e' : '#991b1b' }}>{desktopAiBannerText}</span>
                </div>
              )}

              <HandoffBanner
                key={selectedConversationUserId || 'none'}
                tenantId={tenantId}
                userId={selectedConversationUserId}
                handedOff={selectedHandedOff}
                onResumed={() => refreshThreadAndList(selectedContactId, selectedChannel)}
                sentryAction="resume_handoff_desktop"
              />

              {/* Messages */}
              <div
                ref={messagesContainerRef}
                onScroll={(event) => updateNearBottom(event.currentTarget, desktopIsNearBottomRef)}
                style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}
              >
                {detailLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
                    <div style={{ width: 28, height: 28, border: '2.5px solid #1535c8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  </div>
                ) : (
                  msgs.filter(m => m.role !== 'system').map((m, i) => {
                    const isUser = m.role === 'user'
                    const isStaff = m.role === 'staff'
                    const isZnsDelivery = m.metadata?.zns_delivery === true
                    const isImageOnly = messageHasOnlyMedia(m)
                    return (
                      <div key={m._localId || m.message_id || `${m.timestamp}:${i}`} style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-start' : 'flex-end' }}>
                        <div style={{ maxWidth: '68%', borderRadius: isImageOnly ? 0 : 12, padding: isImageOnly ? 0 : '10px 13px', fontSize: 13, lineHeight: 1.5, background: isImageOnly ? 'transparent' : isUser ? 'white' : isZnsDelivery ? '#fffbeb' : isStaff ? '#f0fdf4' : '#1535c8', color: isUser ? '#111' : isZnsDelivery ? '#92400e' : isStaff ? '#15803d' : 'white', border: isImageOnly ? 'none' : isUser ? '0.5px solid #e0e3f0' : isZnsDelivery ? '0.5px solid #fde68a' : isStaff ? '0.5px solid #bbf7d0' : 'none', borderBottomLeftRadius: isImageOnly ? 0 : (isUser ? 4 : 12), borderBottomRightRadius: isImageOnly ? 0 : (isUser ? 12 : 4), ...(isDeliveryFailed(m) ? { border: '1px solid #ef4444', opacity: 0.75 } : {}) }}>
                          {isZnsDelivery ? <ZnsMessageBody message={m} /> : <MessageContent message={m} imageOnly={isImageOnly} />}
                        </div>
                        {isZnsDelivery && <span style={{ fontSize: 10, color: '#b45309', marginTop: 2 }}>{t('inbox.znsDelivery')}</span>}
                        <QuickAnswerTag message={m} />
                        <MessageDeliveryStatus message={m} onRetry={imageBatchDesktop.retrySend} />
                        {isStaff && <span style={{ fontSize: 10, color: '#16a34a', marginTop: 2 }}>{t('inbox.modeStaff')}</span>}
                        <span style={{ fontSize: 10, color: '#999', marginTop: 3, textAlign: isUser ? 'left' : 'right' }}>{formatTime(m.timestamp)}</span>
                      </div>
                    )
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Images picked but not yet sent (phase A) */}
              <ImageBatchPreview
                items={imageBatchDesktop.items}
                onRetry={imageBatchDesktop.retryUpload}
                onRemove={imageBatchDesktop.remove}
                maxImages={imageBatchDesktop.maxImages}
              />

              <QuickAnswerSuggestion
                tenantId={tenantId}
                userId={selectedConversationUserId}
                canReply={canReply && !selectedIsZbs}
                replyText={replyText}
                onApply={setReplyText}
              />

              {/* Message input */}
              <div style={{ padding: '10px 16px', borderTop: '0.5px solid #e0e3f0', background: 'white', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  ref={desktopImageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(event) => handleBatchPick(imageBatchDesktop, event)}
                  disabled={selectedIsZbs}
                  style={{ display: 'none' }}
                />
                <input
                  ref={desktopFileInputRef}
                  type="file"
                  accept="*/*"
                  onChange={handleDesktopFileChange}
                  disabled={selectedIsZbs}
                  style={{ display: 'none' }}
                />
                <textarea
                  rows={Math.min(replyText.split('\n').length, 5)}
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  onPaste={e => handleReplyPaste(e, setReplyText)}
                  onKeyDown={e => { if (!selectedIsZbs && isSendEnter(e, { allowShift: true })) { e.preventDefault(); handleDesktopPrimarySend() } }}
                  disabled={selectedIsZbs || !selectedConversationUserId || sending || uploadingImage || uploadingFile}
                  style={{ flex: 1, padding: '8px 12px', border: '0.5px solid #e0e3f0', borderRadius: 10, fontSize: 13, background: selectedIsZbs ? '#f1f5f9' : '#f9fafb', color: '#111', outline: 'none', resize: 'none', fontFamily: 'inherit', lineHeight: 1.4 }}
                  placeholder={selectedConversationUserId ? t('inbox.replyPlaceholder') : t('inbox.replyPlaceholderSelect')}
                />
                {sendError && <span style={{ fontSize: 11, color: '#ef4444', flexShrink: 0 }}>{sendError}</span>}
                <div style={{ width: 34, height: 34, position: 'relative', flexShrink: 0 }}>
                  {uploadingFile && (
                    <div style={{ position: 'absolute', inset: -2, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                      <SpinnerRing size={38} color="#1535c8" strokeWidth={2.5} />
                    </div>
                  )}
                  <button
                    onClick={handlePickDesktopFile}
                    disabled={selectedIsZbs || !selectedConversationUserId || sending || uploadingImage || uploadingFile}
                    style={{ width: 34, height: 34, borderRadius: 10, background: '#eef2ff', border: '0.5px solid #c7d2fe', cursor: !selectedIsZbs && selectedConversationUserId && !sending && !uploadingImage && !uploadingFile ? 'pointer' : 'not-allowed', opacity: !selectedIsZbs && selectedConversationUserId && !sending && !uploadingImage && !uploadingFile ? 1 : 0.6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title={t('inbox.sendFile')}
                  >
                    <Paperclip size={15} color="#1535c8" />
                  </button>
                </div>
                <div style={{ width: 34, height: 34, position: 'relative', flexShrink: 0 }}>
                  {uploadingImage ? (
                    <div style={{ position: 'absolute', inset: -2, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                      <SpinnerRing size={38} color="#1535c8" strokeWidth={2.5} />
                    </div>
                  ) : null}
                  <button
                    onClick={handlePickDesktopImage}
                    disabled={selectedIsZbs || !selectedConversationUserId || sending || uploadingImage || uploadingFile}
                    style={{ width: 34, height: 34, borderRadius: '50%', background: '#f3f4f6', border: '0.5px solid #e0e3f0', cursor: !selectedIsZbs && selectedConversationUserId && !sending && !uploadingImage && !uploadingFile ? 'pointer' : 'not-allowed', opacity: !selectedIsZbs && selectedConversationUserId && !sending && !uploadingImage && !uploadingFile ? 1 : 0.6, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                    title={t('inbox.sendImage')}
                  >
                    <Image size={14} color="#374151" />
                  </button>
                </div>
                {(() => {
                  // With images waiting, the button sends the batch and so is
                  // enabled on an empty caption; with an empty tray it keeps
                  // the original "needs text" rule exactly.
                  const hasBatch = imageBatchDesktop.items.length > 0
                  const blocked = selectedIsZbs || !selectedConversationUserId || sending || uploadingImage || uploadingFile
                  const disabled = blocked || imageBatchDesktop.isSending
                    || (hasBatch ? imageBatchDesktop.readyCount === 0 || imageBatchDesktop.isPreparing : !replyText.trim())
                  return (
                    <button
                      onClick={handleDesktopPrimarySend}
                      disabled={disabled}
                      title={hasBatch ? t('inbox.batchSendImages', { count: imageBatchDesktop.readyCount }) : undefined}
                      style={{ minWidth: 34, height: 34, borderRadius: hasBatch ? 17 : '50%', padding: hasBatch ? '0 10px' : 0, background: '#1535c8', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, flexShrink: 0 }}
                    >
                      <Send size={14} color="white" />
                      {hasBatch && <span style={{ fontSize: 12, color: 'white', fontWeight: 500 }}>{imageBatchDesktop.readyCount}</span>}
                    </button>
                  )
                })()}
              </div>
            </>
          )}
        </div>

        {/* ── RIGHT PANEL ── */}
        <div style={{ width: 260, flexShrink: 0, height: '100%', background: 'white', borderLeft: '0.5px solid #e0e3f0', padding: 16, overflowY: 'auto', overflowX: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: '#111' }}>{t('inbox.customerInfo')}</span>
            {conv && !contactIdInvalid && (
              <button
                onClick={() => setEditContactModalOpen(true)}
                title={t('contacts.editContact', { defaultValue: 'Edit contact' })}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, border: 'none', background: 'none', color: '#999', cursor: 'pointer', borderRadius: 6 }}
              >
                <Pencil size={14} />
              </button>
            )}
          </div>

          {!conv ? (
            <p style={{ fontSize: 12, color: '#999', marginTop: 8 }}>{t('inbox.selectForDetails')}</p>
          ) : (
            <>
              {/* Avatar */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 14, borderBottom: '0.5px solid #f0f0f0' }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#e0e7ff', color: '#1535c8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 600 }}>
                  {(conv.name || '?').charAt(0).toUpperCase()}
                </div>
                <p style={{ textAlign: 'center', fontSize: 14, fontWeight: 500, color: '#111', marginTop: 8 }}>{conv.name}</p>
              </div>

              <div style={{ marginTop: 14 }}>
                {basicInfoRows.map(row => (
                  <BasicInfoRow key={row.label} label={row.label} value={row.value} truncate={row.truncate} />
                ))}
              </div>

              <ContactTagsPanel
                tenantId={tenantId}
                contactId={conv.contact_id}
                tags={conv.assignedTags ?? conv.tags}
                variant="inbox"
                disabled={contactIdInvalid}
                onTagsChange={(next) => {
                  patchConversationCaches(prev => prev.map(c =>
                    c.contact_id === conv.contact_id ? { ...c, assignedTags: next, tags: next } : c
                  ))
                  void queryClient.invalidateQueries({ queryKey: inboxKeys.conversations(tenantId) })
                }}
              />
              <ContactNotesPanel
                notes={conv.notes}
                disabled={contactIdInvalid}
                saving={notesSavingContactId === conv.contact_id}
                onSave={(nextNotes) => handleSaveContactNotes(conv, nextNotes)}
                t={t}
              />

              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '0.5px solid #f0f0f0' }}>
                <p style={{ fontSize: 11, color: '#999', margin: '0 0 6px' }}>{t('inbox.assignedStaff')}</p>
                <p style={{ margin: 0, fontSize: 12, color: '#111' }}>
                  {staffDisplayName(staffList.find(staff => staff.id === activeAssignedStaffId)) || t('inbox.unassigned')}
                </p>
                <p style={{ margin: '6px 0 0', fontSize: 10, color: '#999' }}>
                  {assignmentLabel(selectedAssignment, t)}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
      <EditContactModal
        open={editContactModalOpen}
        onClose={() => setEditContactModalOpen(false)}
        contact={editContact}
        fields={INBOX_EDIT_CONTACT_FIELDS}
        t={t}
        onSave={patch => handleSaveContactEdit(conv, patch)}
      />
      </DesktopShell>
    )
  }

  // ════════════════════════════════════════════
  // MOBILE LAYOUT
  // ════════════════════════════════════════════

  // ── Thread view ──
  if (mobileView === 'thread' && mobileConv) {
    const msgs = mobileDetail?.messages ?? []
    const mobileAssignment = getChannelMeta(mobileConv, mobileSelectedChannel) || mobileConv || {}
    const mobileAssignedStaffId = mobileAssignment?.assigned_staff_id || ''
    const mobileBotReplying = isBotReplying({ aiEnabled: mobileAiEnabled, handedOff: mobileHandedOff })
    const mobileEffectiveAiEnabled = mobileBotReplying && !mobileAiToggleDisabled
    const mobileAiBannerText = mobileAiToggleDisabled
      ? t('inbox.channelBotDisabledBannerShort', { defaultValue: 'Channel bot off - manual handling' })
      : t('inbox.disabledBannerShort')
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#f2f4f8', display: 'flex', flexDirection: 'column', zIndex: 50 }}>

        {/* Header */}
        <div style={{ background: '#1535c8', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => patchInboxUi(tenantId, { mobileView: 'list', mobileContactId: null, mobileSelectedChannel: null })}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', flexShrink: 0 }}
            >
              <ArrowLeft size={20} color="white" />
            </button>

            <div style={{ flexShrink: 0, filter: 'brightness(1.15)' }}>
              <ConversationAvatar channels={mobileConv.channels} name={mobileConv.name} avatarUrl={mobileConv.avatar_url} />
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 15, fontWeight: 600, color: 'white', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mobileConv.name}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: mobileEffectiveAiEnabled ? '#4ade80' : '#fbbf24', display: 'inline-block', flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: mobileEffectiveAiEnabled ? '#4ade80' : '#fbbf24', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {mobileAiToggleDisabled
                    ? t('inbox.channelChatbotDisabled', { defaultValue: 'Channel bot off' })
                    : (mobileBotReplying ? t('inbox.chatbotActive') : t('inbox.chatbotDisabled'))}
                </span>
              </div>
              <div style={{ marginTop: 6 }}>
                <ChannelToggle
                  channels={mobileConv.channels}
                  selectedChannel={mobileSelectedChannel}
                  unreadByChannel={mobileConv.unreadByChannel}
                  onSelect={ch => openMobileThread(mobileConv, ch)}
                  t={t}
                  compact
                />
              </div>
            </div>

            {/* AI toggle pill */}
            <div
              onClick={handleMobileAIToggle}
              title={mobileAiToggleDisabled ? t('inbox.channelBotDisabledTooltip', { defaultValue: 'Chatbot is disabled at channel level. Enable it in channel settings to use AI.' }) : undefined}
              style={{ width: 44, height: 24, borderRadius: 12, background: mobileEffectiveAiEnabled ? '#4ade80' : 'rgba(255,255,255,0.3)', cursor: mobileAiToggleDisabled ? 'not-allowed' : 'pointer', opacity: mobileAiToggleDisabled ? 0.65 : 1, position: 'relative', flexShrink: 0, transition: 'background 0.2s, opacity 0.2s' }}
            >
              <div style={{ width: 20, height: 20, background: 'white', borderRadius: '50%', position: 'absolute', top: 2, left: 2, transition: 'transform 0.2s', transform: mobileEffectiveAiEnabled ? 'translateX(20px)' : 'translateX(0)' }} />
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.72)' }}>{t('inbox.assignStaff')}</span>
            <select
              value={mobileAssignedStaffId}
              disabled={!mobileSelectedMeta?.conversation_id || assigningStaff}
              onChange={e => handleAssignMobileConversation(e.target.value)}
              style={{
                minWidth: 0,
                flex: 1,
                height: 34,
                border: '0.5px solid rgba(255,255,255,0.28)',
                borderRadius: 9,
                background: 'rgba(255,255,255,0.14)',
                color: 'white',
                fontSize: 12,
                fontWeight: 600,
                padding: '0 10px',
                outline: 'none',
                cursor: mobileSelectedMeta?.conversation_id && !assigningStaff ? 'pointer' : 'not-allowed',
                opacity: mobileSelectedMeta?.conversation_id && !assigningStaff ? 1 : 0.65,
              }}
            >
              <option style={{ color: '#111' }} value="">{t('inbox.unassigned')}</option>
              {staffList.map(staff => (
                <option style={{ color: '#111' }} key={staff.id} value={staff.id}>{staffDisplayName(staff)}</option>
              ))}
            </select>
          </label>
        </div>

        {/* Disabled banner */}
        {(mobileAiToggleDisabled || !mobileAiEnabled) && (
          <div style={{ background: mobileAiToggleDisabled ? '#fffbeb' : '#fef2f2', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <AlertCircle size={14} color={mobileAiToggleDisabled ? '#b45309' : '#991b1b'} />
            <span style={{ fontSize: 12, color: mobileAiToggleDisabled ? '#92400e' : '#991b1b' }}>{mobileAiBannerText}</span>
          </div>
        )}

        <HandoffBanner
          key={mobileSelectedUserId || 'none'}
          tenantId={tenantId}
          userId={mobileSelectedUserId}
          handedOff={mobileHandedOff}
          onResumed={() => refreshThreadAndList(mobileConv?.contact_id, mobileSelectedChannel)}
          sentryAction="resume_handoff_mobile"
        />

        {/* Messages */}
        <div
          ref={mobileMessagesContainerRef}
          onScroll={(event) => updateNearBottom(event.currentTarget, mobileIsNearBottomRef)}
          style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}
        >
          {mobileDetailLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
              <div style={{ width: 28, height: 28, border: '2.5px solid #1535c8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : (
            msgs.filter(m => m.role !== 'system').map((m, i) => {
              const isUser = m.role === 'user'
              const isZnsDelivery = m.metadata?.zns_delivery === true
              const isImageOnly = messageHasOnlyMedia(m)
              return (
                <div key={m._localId || m.message_id || `${m.timestamp}:${i}`} style={{ display: 'flex', flexDirection: 'column', alignSelf: isUser ? 'flex-start' : 'flex-end', maxWidth: '80%' }}>
                  <div style={{ borderRadius: isImageOnly ? 0 : (isUser ? '18px 18px 18px 4px' : '18px 18px 4px 18px'), padding: isImageOnly ? 0 : '10px 14px', fontSize: 14, background: isImageOnly ? 'transparent' : isUser ? 'white' : isZnsDelivery ? '#fffbeb' : '#1535c8', color: isUser ? '#111' : isZnsDelivery ? '#92400e' : 'white', border: isImageOnly ? 'none' : isZnsDelivery ? '0.5px solid #fde68a' : 'none', boxShadow: isImageOnly ? 'none' : (isUser ? '0 1px 2px rgba(0,0,0,0.06)' : 'none'), ...(isDeliveryFailed(m) ? { border: '1px solid #ef4444', opacity: 0.75 } : {}) }}>
                    {isZnsDelivery ? <ZnsMessageBody message={m} /> : <MessageContent message={m} imageOnly={isImageOnly} />}
                  </div>
                  {isZnsDelivery && <span style={{ fontSize: 10, color: '#b45309', marginTop: 2 }}>{t('inbox.znsDelivery')}</span>}
                  <QuickAnswerTag message={m} />
                  <MessageDeliveryStatus message={m} onRetry={imageBatchMobile.retrySend} />
                  <span style={{ fontSize: 11, color: '#999', marginTop: 3, textAlign: isUser ? 'left' : 'right' }}>
                    {formatTime(m.timestamp)}
                  </span>
                </div>
              )
            })
          )}
          <div ref={mobileMessagesEndRef} />
        </div>

        {/* Images picked but not yet sent (phase A) */}
        <ImageBatchPreview
          items={imageBatchMobile.items}
          onRetry={imageBatchMobile.retryUpload}
          onRemove={imageBatchMobile.remove}
          maxImages={imageBatchMobile.maxImages}
          compact
        />

        <QuickAnswerSuggestion
          tenantId={tenantId}
          userId={mobileSelectedUserId}
          canReply={canReply && !mobileSelectedIsZbs}
          replyText={mobileReplyText}
          onApply={setMobileReplyText}
        />

        {/* Input row */}
        <div style={{
          flexShrink: 0,
          background: 'white',
          borderTop: '0.5px solid #e0e3f0',
          paddingTop: 10,
          paddingLeft: 14,
          paddingRight: 14,
          paddingBottom: 'calc(14px + env(safe-area-inset-bottom, 16px))',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <input
            ref={mobileImageInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(event) => handleBatchPick(imageBatchMobile, event)}
            disabled={mobileSelectedIsZbs}
            style={{ display: 'none' }}
          />
          <input ref={mobileFileInputRef} type="file" accept="*/*" onChange={handleMobileFileChange} disabled={mobileSelectedIsZbs} style={{ display: 'none' }} />
          <div style={{ width: 28, height: 28, position: 'relative', flexShrink: 0 }}>
            {mobileUploadingFile && <div style={{ position: 'absolute', inset: -4, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}><SpinnerRing size={36} color="#1535c8" strokeWidth={2.5} /></div>}
            <button onClick={handlePickMobileFile} disabled={mobileSelectedIsZbs || !mobileSelectedUserId || mobileSending || mobileUploadingImage || mobileUploadingFile}
              style={{ width: 28, height: 28, border: 'none', borderRadius: 8, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: mobileSelectedIsZbs || !mobileSelectedUserId || mobileSending || mobileUploadingImage || mobileUploadingFile ? 'not-allowed' : 'pointer', opacity: mobileSelectedIsZbs || !mobileSelectedUserId || mobileSending || mobileUploadingImage || mobileUploadingFile ? 0.4 : 1 }}
              title={t('inbox.sendFile')}>
              <Paperclip size={18} color="#1535c8" />
            </button>
          </div>
          <div style={{ width: 28, height: 28, position: 'relative', flexShrink: 0 }}>
            {mobileUploadingImage ? (
              <div style={{ position: 'absolute', inset: -4, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                <SpinnerRing size={36} color="#1535c8" strokeWidth={2.5} />
              </div>
            ) : null}
            <button
              onClick={handlePickMobileImage}
              disabled={mobileSelectedIsZbs || !mobileSelectedUserId || mobileSending || mobileUploadingImage || mobileUploadingFile}
              style={{ width: 28, height: 28, border: 'none', background: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: mobileSelectedIsZbs || !mobileSelectedUserId || mobileSending || mobileUploadingImage || mobileUploadingFile ? 'not-allowed' : 'pointer', opacity: mobileSelectedIsZbs || !mobileSelectedUserId || mobileSending || mobileUploadingImage || mobileUploadingFile ? 0.4 : 1, flexShrink: 0 }}
              title={t('inbox.sendImage')}
            >
              <Image size={20} color="#666" />
            </button>
          </div>
          <textarea rows={Math.min(mobileReplyText.split('\n').length, 5)} value={mobileReplyText} onChange={e => setMobileReplyText(e.target.value)}
            onPaste={e => handleReplyPaste(e, setMobileReplyText)}
            onKeyDown={e => { if (!mobileSelectedIsZbs && isSendEnter(e)) { e.preventDefault(); handleMobilePrimarySend() } }}
            disabled={mobileSelectedIsZbs || mobileSending || mobileUploadingImage || mobileUploadingFile}
            style={{ flex: 1, background: mobileSelectedIsZbs ? '#e5e7eb' : '#f2f4f8', border: 'none', borderRadius: 20, padding: '8px 14px', fontSize: 14, color: '#111', outline: 'none', resize: 'none', fontFamily: 'inherit', lineHeight: 1.4 }}
            placeholder={t('inbox.mobileReplyPh')} />
          {(() => {
            const hasBatch = imageBatchMobile.items.length > 0
            const blocked = mobileSelectedIsZbs || !mobileSelectedUserId || mobileSending || mobileUploadingImage || mobileUploadingFile
            const disabled = blocked || imageBatchMobile.isSending
              || (hasBatch ? imageBatchMobile.readyCount === 0 || imageBatchMobile.isPreparing : !mobileReplyText.trim())
            return (
              <button
                onClick={handleMobilePrimarySend}
                disabled={disabled}
                title={hasBatch ? t('inbox.batchSendImages', { count: imageBatchMobile.readyCount }) : undefined}
                style={{ minWidth: 46, height: 46, borderRadius: 23, padding: hasBatch ? '0 12px' : 0, background: '#1535c8', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexShrink: 0 }}
              >
                <Send size={18} color="white" />
                {hasBatch && <span style={{ fontSize: 14, color: 'white', fontWeight: 500 }}>{imageBatchMobile.readyCount}</span>}
              </button>
            )
          })()}
        </div>
      </div>
    )
  }

  // ── List view ──
  return (
    <AppShell title={t('inbox.title')}>
      <div className="bg-[#f2f4f8]">

        {/* Section 1 — Search + filter bar */}
        <div className="sticky top-0 bg-[#f2f4f8] pt-2 pb-3 z-20 flex gap-2">
          <div className="flex-1 bg-white rounded-xl px-3 py-2.5 flex items-center gap-2" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <Search size={14} className="text-zinc-400" />
            <input
              className="flex-1 text-sm text-gray-800 placeholder-gray-400 outline-none border-none bg-transparent"
              placeholder={t('inbox.searchPlaceholder')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          <button
            className="w-24 bg-white rounded-xl px-3 py-2.5 flex items-center justify-between text-sm font-semibold text-gray-700"
            style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
            onClick={() => setShowChannelSheet(true)}
          >
            <span>{currentFilterLabel}</span>
            <ChevronDown size={12} className="text-zinc-500" />
          </button>
        </div>

        <div className="flex gap-2 pb-3 overflow-x-auto">
          {filterChips.map(chip => {
            const selected = listFilter === chip.value
            return (
              <button
                key={chip.value}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold whitespace-nowrap"
                style={{
                  border: `0.5px solid ${selected ? '#1535c8' : '#e0e3f0'}`,
                  background: selected ? '#e8ecff' : 'white',
                  color: selected ? '#1535c8' : '#666',
                }}
                onClick={() => setListFilter(selected && chip.value !== 'all' ? 'all' : chip.value)}
              >
                {chip.label}
              </button>
            )
          })}
        </div>

        {/* Section 2 — Staff filter (collapsible, default closed) */}
        <button
          className="flex items-center gap-1 text-xs font-semibold text-gray-500 pb-2"
          onClick={() => setStaffSectionOpen(open => !open)}
        >
          <span>{t('inbox.staffSection')}{staffFilter !== 'all' ? ' •' : ''}</span>
          <ChevronDown size={12} className="text-zinc-500" style={{ transform: staffSectionOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
        </button>
        {staffSectionOpen && (
          <div className="scrollbar-hidden flex gap-2 pb-3 overflow-x-auto">
            <StaffFilterChip
              active={staffFilter === 'all'}
              label={t('inbox.allStaff')}
              count={null}
              onClick={() => setStaffFilter('all')}
            />
            <StaffFilterChip
              active={staffFilter === UNASSIGNED_STAFF_FILTER}
              label={t('inbox.unassigned')}
              count={null}
              onClick={() => setStaffFilter(UNASSIGNED_STAFF_FILTER)}
            />
            {staffList.map(staff => (
              <StaffFilterChip
                key={staff.id}
                active={staffFilter === staff.id}
                label={staffDisplayName(staff)}
                count={staff.assigned_count || 0}
                onClick={() => setStaffFilter(staff.id)}
              />
            ))}
            {staffLoading && (
              <span className="text-xs text-gray-400 self-center whitespace-nowrap px-1">
                {t('common.loading')}
              </span>
            )}
          </div>
        )}

        <div>
          {conversationsQuery.isPending && (
            <div className="flex justify-center py-8"><SpinnerRing size={28} color="#1535c8" /></div>
          )}
          {filtered.map(conv => {
            const unreadCount = Number(conv.unread || 0)
            const assignmentText = assignmentLabel(conv, t)
            const hasAssignment = Boolean(conv.assigned_staff_id || conv.assigned_staff_name || conv.assigned_staff_email)
            return (
              <button
                key={conv.id}
                className="w-full text-left bg-white rounded-2xl p-3 mb-2.5 flex items-center gap-3"
                style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
                onClick={() => openMobileThread(conv)}
              >
                <ConversationAvatar channels={conv.channels} name={conv.name} avatarUrl={conv.avatar_url} size={44} />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-sm font-bold text-gray-900 truncate">{conv.name}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <div className="text-xs text-gray-400 truncate flex-1 min-w-0">{conv.preview}</div>
                    <UnreadCountBadge count={unreadCount} compact />
                  </div>
                </div>

                <div className="flex-shrink-0 flex flex-col items-end gap-1">
                  <span className="text-xs text-gray-400">{conv.time}</span>
                  {conv.mode !== 'ai' && <ModePill mode={conv.mode} t={t} />}
                  <HandoffPill handedOff={isContactHandedOff(conv)} />
                  {hasAssignment && (
                    <span
                      title={assignmentText}
                      className="max-w-[108px] truncate rounded-full px-2 py-0.5 text-[10px] font-semibold"
                      style={{ background: '#f3e8ff', color: '#7e22ce' }}
                    >
                      {assignmentText}
                    </span>
                  )}
                  <AiBotStatusDot mode={conv.mode} channelBotEnabled={conv.channel_bot_enabled} handedOff={isContactHandedOff(conv)} t={t} />
                </div>
              </button>
            )
          })}
          {/* B-FE7/B-FE9 — mobile list is normal page flow (AppShell owns
              the scroll), so IntersectionObserver's default root (the page
              viewport) is already correct; sidebarScrollRootRef is left
              unset here (see its declaration) on purpose. Same
              hide-when-no-more-pages rule as the desktop sentinel above. */}
          {conversationsQuery.hasNextPage && (
            <div ref={loadMoreSentinelRef} className="flex justify-center py-4">
              {conversationsQuery.isFetchingNextPage && <SpinnerRing size={20} color="#1535c8" />}
            </div>
          )}
        </div>
      </div>

      {/* Section 3 — Channel filter sheet */}
      {showChannelSheet && (
        <>
          <div className="overlay" onClick={() => setShowChannelSheet(false)} />
          <div className="bottom-sheet">
            <div className="sheet-handle" />
            <p className="text-gray-900 font-bold text-base mb-4">{t('inbox.channelSheetTitle')}</p>
            <div>
              {channelOptions.map((opt, idx) => {
                const selected = channelFilter === opt.value
                return (
                  <button
                    key={opt.value}
                    className={`w-full py-3 flex items-center justify-between text-sm text-left${
                      idx < channelOptions.length - 1 ? ' border-b border-gray-100' : ''
                    }`}
                    onClick={() => { setChannelFilter(opt.value); setShowChannelSheet(false) }}
                  >
                    <span style={{ color: selected ? '#1535c8' : undefined, fontWeight: selected ? 600 : undefined }}>
                      {opt.label}
                    </span>
                    {selected && (
                      <Check size={16} color="#1535c8" />
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}
    </AppShell>
  )
}
